import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ProductLanguageHelper from "@/i18n/ProductLanguageHelper";
import SettingsTabSlider from "@/components/SettingsTabSlider";
import api, { extractErr, mediaUrl, platformDisplayHost } from "@/lib/api";
import { toast } from "sonner";
import { useMerchantAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, Save, Plus, Trash2, ChevronUp, ChevronDown, GripVertical, Info, ShieldCheck, Loader2, Truck, IndianRupee, Headset, Star, Sparkles, Award, Heart, Clock, Package, Leaf, Zap, Gift, ThumbsUp, BadgeCheck, ShoppingBag, Phone, MapPin, Thermometer, Wind, Droplets, Shirt, Snowflake, Flame, Recycle, Waves, Megaphone, Bell, Tag, Percent } from "lucide-react";
import { GOOGLE_FONTS, fontStack, googleFontHref } from "@/lib/googleFonts";
import { WHY_US_LUCIDE_NAMES, WHY_US_EMOJIS, isWhyUsEmoji, ANNOUNCEMENT_LUCIDE_NAMES } from "@/lib/whyUsIcons";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "@/i18n/languages";
import TutorialBanner, { SECTION_TITLES, useSectionTutorials } from "@/components/TutorialBanner";
import { useFileUpload } from "@/lib/uploadProgress";
import { isMp4Url } from "@/lib/mediaKind";
import { IN_STATES } from "@/lib/indiaShipping";
import SitePreloader from "@/components/SitePreloader";
import { PRELOADERS, normalizePreloader } from "@/lib/preloaders";

const WHY_US_LUCIDE = {
  Megaphone, Bell, Tag, Percent,
  Truck, ShieldCheck, IndianRupee, Headset, Star, Sparkles, Award, Heart,
  Clock, Package, Leaf, Zap, Gift, ThumbsUp, BadgeCheck, ShoppingBag, Phone, MapPin,
  Thermometer, Wind, Droplets, Shirt, Snowflake, Flame, Recycle, Waves,
};

function WhyUsIconPreview({ name }) {
  if (!name) return <span className="text-[11px] text-slate-400">None</span>;
  if (isWhyUsEmoji(name)) return <span className="text-lg leading-none">{name}</span>;
  const C = WHY_US_LUCIDE[name] || Star;
  return <C className="w-4 h-4" />;
}

function WhyUsIconPicker({ value, onChange, testId, allowNone, names }) {
  const list = names || WHY_US_LUCIDE_NAMES;
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5">
        {allowNone ? (
          <button
            type="button"
            title="No icon"
            onClick={() => onChange("")}
            className={`h-8 px-2 rounded-lg border text-[11px] font-semibold ${value === "" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
            data-testid={testId ? `${testId}-none` : undefined}
          >
            No icon
          </button>
        ) : null}
        {list.map((id) => {
          const C = WHY_US_LUCIDE[id];
          const on = value === id;
          return (
            <button
              key={id}
              type="button"
              title={id}
              onClick={() => onChange(id)}
              className={`w-8 h-8 rounded-lg border flex items-center justify-center ${on ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
            >
              {C ? <C className="w-4 h-4" /> : id}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {WHY_US_EMOJIS.map((em) => {
          const on = value === em;
          return (
            <button
              key={em}
              type="button"
              onClick={() => onChange(em)}
              className={`w-8 h-8 rounded-lg border text-base leading-none flex items-center justify-center ${on ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              {em}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function announcementSlides(ann) {
  const a = ann || {};
  if (Array.isArray(a.slides) && a.slides.length) return a.slides;
  return [{ text: a.text || "", link: a.link || "", icon: a.icon === undefined ? "Megaphone" : (a.icon || "") }];
}

function ShiprocketBox({ sr, setSr }) {
  const [testing, setTesting] = useState(false);
  const [pickups, setPickups] = useState(null);
  const [pickupMatch, setPickupMatch] = useState(null);
  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/merchant/shipping/shiprocket/test");
      setPickups(data.pickups || []);
      setPickupMatch(data.pickup_matches);
      if (data.pickup_matches === false) {
        toast.error("Pickup nickname does not match Shiprocket. Pick one of the names below.");
      } else {
        toast.success("Shiprocket login works");
      }
    } catch (e) {
      setPickups(null);
      toast.error(extractErr(e));
    } finally {
      setTesting(false);
    }
  };
  return (
    <div className="p-4 rounded-lg bg-slate-50 space-y-3" data-testid="shiprocket-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold text-slate-900">Shiprocket</div>
          <div className="text-xs text-slate-500">We push orders to Shiprocket over their API. You do not connect a store channel or set a webhook for order sync.</div>
        </div>
        <Switch checked={Boolean(sr.enabled)} onCheckedChange={(v) => setSr({ enabled: v })} data-testid="sr-enabled" />
      </div>
      {sr.enabled && (
        <>
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 space-y-1.5" data-testid="sr-help">
            <p><b>Correct flow:</b> save API email + password → pickup nickname must match Shiprocket → confirm the order (or wait for prepaid payment) → it appears in Shiprocket Orders. Use <b>Create shipment</b> on an order if a row is missing.</p>
            <p>Shiprocket <b>Webhooks</b> and <b>Connect Channel</b> are not used for creating orders. Leave the webhook URL empty. Do not paste your store domain there.</p>
            <p><b>COD / WhatsApp:</b> status stays Placed until you mark the order <b>Confirmed</b>. Auto-create only runs on Confirmed.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>API email</Label><Input data-testid="sr-email" className="mt-1" value={sr.email || ""} onChange={(e) => setSr({ email: e.target.value })} placeholder="Shiprocket API user email" /></div>
            <div>
              <Label className="flex items-center gap-2">API password
                {sr.has_password && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                {sr.has_password && <button type="button" onClick={() => setSr({ password: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
              </Label>
              <Input type="password" data-testid="sr-password" className="mt-1" value={sr.password === "__CLEAR__" ? "" : (sr.password || "")} onChange={(e) => setSr({ password: e.target.value })} placeholder={sr.has_password ? "•••••• (leave blank to keep)" : "API user password from Shiprocket"} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Pickup location nickname</Label>
              <Input data-testid="sr-pickup" className="mt-1" value={sr.pickup_location || ""} onChange={(e) => setSr({ pickup_location: e.target.value })} placeholder="Primary" />
              <p className="text-[11px] text-slate-500 mt-1">Must match the nickname in Shiprocket → Settings → Pickup addresses (usually <b>Primary</b>). Do not paste the street address.</p>
            </div>
            <div>
              <Label>Channel ID <span className="text-slate-400 font-normal">leave blank</span></Label>
              <Input data-testid="sr-channel" className="mt-1 font-mono-nums" value={sr.channel_id || ""} onChange={(e) => setSr({ channel_id: e.target.value })} placeholder="Optional Custom/API channel only" />
              <p className="text-[11px] text-slate-500 mt-1">Leave empty unless Shiprocket gave you a Custom API channel. A Shopify/WooCommerce channel ID will hide or fail orders.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={testConn} disabled={testing} data-testid="sr-test">
              {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Test connection & list pickups
            </Button>
            <span className="text-[11px] text-slate-500">Save shipping first, then test.</span>
          </div>
          {pickups && (
            <div className="text-xs text-slate-700" data-testid="sr-pickups">
              {pickups.length === 0 ? "Login worked, but we could not read pickup nicknames. Use the nickname from Shiprocket → Pickup addresses (not the street). Then Save shipping." : (
                <>
                  <div className="font-medium mb-1">Pickup nicknames on this account:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {pickups.map((p) => (
                      <button key={p} type="button" className="px-2 py-0.5 rounded border border-slate-200 bg-white hover:border-blue-400" onClick={() => setSr({ pickup_location: p })}>{p}</button>
                    ))}
                  </div>
                  {pickupMatch === false ? <p className="mt-1 text-red-600">Your saved nickname does not match any of these.</p> : null}
                </>
              )}
            </div>
          )}
          <div className="space-y-2 pt-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Features</div>
            <label className="flex items-center justify-between gap-3 text-sm"><span>Auto-create shipment when an order is confirmed</span><Switch checked={Boolean(sr.auto_create_shipment)} onCheckedChange={(v) => setSr({ auto_create_shipment: v })} data-testid="sr-auto" /></label>
            <label className="flex items-center justify-between gap-3 text-sm"><span>Request AWB / shipping label after create</span><Switch checked={Boolean(sr.generate_label)} onCheckedChange={(v) => setSr({ generate_label: v })} data-testid="sr-label" /></label>
            <label className="flex items-center justify-between gap-3 text-sm"><span>Show tracking / AWB on the order page</span><Switch checked={sr.show_tracking !== false} onCheckedChange={(v) => setSr({ show_tracking: v })} data-testid="sr-tracking" /></label>
          </div>
        </>
      )}
    </div>
  );
}

function MenuEditor({ title, subtitle, items, onChange, prefix }) {
  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    onChange(next);
  };
  const patch = (i, k, v) => { const next = [...items]; next[i] = { ...next[i], [k]: v }; onChange(next); };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { label: "New link", path: "", children: [], open_in_new_tab: false }]);
  const isHttp = (p) => /^https?:\/\//i.test(p || "");
  const newTabChecked = (it) => (it.open_in_new_tab === true) || (it.open_in_new_tab !== false && isHttp(it.path));
  const patchChild = (i, j, k, v) => {
    const next = [...items];
    const children = [...(next[i].children || [])];
    children[j] = { ...children[j], [k]: v };
    next[i] = { ...next[i], children };
    onChange(next);
  };
  const addChild = (i) => {
    const next = [...items];
    next[i] = { ...next[i], children: [...(next[i].children || []), { label: "New submenu", path: "", open_in_new_tab: false }] };
    onChange(next);
  };
  const removeChild = (i, j) => {
    const next = [...items];
    next[i] = { ...next[i], children: (next[i].children || []).filter((_, idx) => idx !== j) };
    onChange(next);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500">{subtitle}</div>
        </div>
        <Button size="sm" variant="outline" onClick={add} data-testid={`${prefix}-add`}><Plus className="w-4 h-4 mr-1" />Add link</Button>
      </div>
      <div className="space-y-1.5">
        {items.length === 0 && <div className="text-sm text-slate-400 italic px-3 py-2 bg-slate-50 rounded-lg">No links yet — press Add link to start.</div>}
        {items.map((it, i) => (
          <div key={i} className="p-2 rounded-lg bg-slate-50 space-y-2" data-testid={`${prefix}-item-${i}`}>
            <div className="flex items-center gap-2">
              <div className="flex flex-col text-slate-500">
                <button type="button" onClick={() => move(i, i - 1)} className="hover:text-slate-800 disabled:opacity-40" disabled={i === 0} data-testid={`${prefix}-item-${i}-up`}><ChevronUp className="w-4 h-4" /></button>
                <button type="button" onClick={() => move(i, i + 1)} className="hover:text-slate-800 disabled:opacity-40" disabled={i === items.length - 1} data-testid={`${prefix}-item-${i}-down`}><ChevronDown className="w-4 h-4" /></button>
              </div>
              <Input value={it.label || ""} onChange={(e) => patch(i, "label", e.target.value)} placeholder="Label (e.g. Contact)" className="flex-1" data-testid={`${prefix}-item-${i}-label`} />
              <Input value={it.path || ""} onChange={(e) => patch(i, "path", e.target.value)} placeholder="path (categories, page/about, /category/sale, https://…)" className="flex-1 font-mono-nums" data-testid={`${prefix}-item-${i}-path`} />
              <Button type="button" size="sm" variant="outline" onClick={() => addChild(i)} data-testid={`${prefix}-item-${i}-add-sub`}>Submenu</Button>
              <button type="button" onClick={() => remove(i)} className="text-red-600 px-2" data-testid={`${prefix}-item-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 ml-8 cursor-pointer select-none">
              <input type="checkbox" checked={newTabChecked(it)} onChange={(e) => patch(i, "open_in_new_tab", e.target.checked)} data-testid={`${prefix}-item-${i}-new-tab`} />
              Open in a new tab
            </label>
            {(it.children || []).map((ch, j) => (
              <div key={j} className="ml-8 space-y-1" data-testid={`${prefix}-item-${i}-sub-${j}`}>
                <div className="flex items-center gap-2">
                  <Input value={ch.label || ""} onChange={(e) => patchChild(i, j, "label", e.target.value)} placeholder="Submenu label" className="flex-1" data-testid={`${prefix}-item-${i}-sub-${j}-label`} />
                  <Input value={ch.path || ""} onChange={(e) => patchChild(i, j, "path", e.target.value)} placeholder="path" className="flex-1 font-mono-nums" data-testid={`${prefix}-item-${i}-sub-${j}-path`} />
                  <button type="button" onClick={() => removeChild(i, j)} className="text-red-600 px-2" data-testid={`${prefix}-item-${i}-sub-${j}-remove`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                  <input type="checkbox" checked={newTabChecked(ch)} onChange={(e) => patchChild(i, j, "open_in_new_tab", e.target.checked)} data-testid={`${prefix}-item-${i}-sub-${j}-new-tab`} />
                  Open in a new tab
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-2 text-xs text-slate-500">
        Path examples · <span className="font-mono">categories</span> · <span className="font-mono">page/about</span> · <span className="font-mono">/category/sale</span> · full URL like <span className="font-mono">https://…</span>
        <span className="block mt-1">Full URLs used to always open in a new tab. Use <strong>Open in a new tab</strong> on each link to keep the shopper on the same tab or send them to a new one.</span>
        <span className="block mt-1">Use <strong>Submenu</strong> to nest links under a parent item (shown as a dropdown on desktop).</span>
      </div>
    </div>
  );
}


function UploadImage({ kind, value, onChange, testid, hint, accept = "image/*" }) {
  const { t } = useTranslation();
  const uploadFile = useFileUpload();
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    setUploading(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      onChange(data.url);
      toast.success(t("common.uploaded"));
    } catch (e) { toast.error(extractErr(e)); } finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-x-3 gap-y-1 flex-wrap">
      {value && (isMp4Url(value) ? (
        <video src={mediaUrl(value)} muted className="w-16 h-16 rounded-lg object-cover border border-slate-200 bg-slate-900" />
      ) : (
        <img src={mediaUrl(value)} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
      ))}
      <div>
        <label className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 cursor-pointer" data-testid={testid}>
          <Upload className="w-4 h-4" /> {uploading ? t("common.uploading") : (value ? t("common.replace") : t("common.upload"))}
          <input type="file" hidden accept={accept} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
        {hint ? <p className="text-[11px] text-slate-400 mt-0.5 leading-snug" data-testid={`${testid}-hint`}>{hint}</p> : null}
      </div>
      {value && <button className="text-xs text-red-600" onClick={() => onChange(null)} data-testid={`${testid}-remove`}>{t("common.remove")}</button>}
    </div>
  );
}

export default function AdminSettings() {
  const { tenant, refresh } = useMerchantAuth();
  const { t, i18n } = useTranslation();
  const [cfg, setCfg] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [domVerifying, setDomVerifying] = useState(false);
  const [tutorial, setTutorial] = useState(null);
  const [hpProducts, setHpProducts] = useState([]);
  const [searchParams] = useSearchParams();
  const [settingsTab, setSettingsTab] = useState(() => {
    if (searchParams.get("tab") === "tax") return "tax";
    try {
      if (sessionStorage.getItem("admin-settings-tab") === "domain") {
        sessionStorage.removeItem("admin-settings-tab");
        return "domain";
      }
    } catch { /* ignore */ }
    return "store_info";
  });
  const sectionTutorials = useSectionTutorials();

  const reloadDashboardForDomain = async () => {
    try { sessionStorage.setItem("admin-settings-tab", "domain"); } catch { /* ignore */ }
    try { await refresh(); } catch { /* still reload so /auth/merchant/me is read */ }
    window.location.reload();
  };

  const verifyDomain = async ({ reload = false } = {}) => {
    setDomVerifying(true);
    try {
      const { data } = await api.post("/merchant/config/domain/verify");
      setCfg((c) => ({ ...c, custom_domain_verified: data.verified }));
      if (data.verified) {
        toast.success("DNS verified — your domain is live!");
      } else {
        const got = (data.resolved_ips || []).length ? (data.resolved_ips || []).join(", ") : "not pointing to us yet";
        const want = data.cname_target || data.expected_ip;
        toast.error(`Not verified yet. DNS currently: ${got}. Point CNAME to ${want} (or keep an A record to ${data.expected_ip}). DNS can take 5–30 min.`);
      }
      if (reload) await reloadDashboardForDomain();
    } catch (e) {
      toast.error(extractErr(e));
      if (reload) await reloadDashboardForDomain();
    } finally {
      setDomVerifying(false);
    }
  };

  useEffect(() => { api.get("/merchant/config").then(({ data }) => setCfg({ ...data, _saved_custom_domain: data.custom_domain || null })); }, []);
  useEffect(() => { api.get("/merchant/products").then(({ data }) => setHpProducts(Array.isArray(data) ? data : [])).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/i18n/languages").then(({ data }) => {
      const code = (i18n.language || "en").split("-")[0];
      setTutorial((data.tutorials || {})[code] || null);
    }).catch(() => {});
  }, [i18n.language]);

  if (!cfg) return <div className="text-slate-500">{t("common.loading")}</div>;

  const setPart = (key, val) => setCfg({ ...cfg, [key]: val });
  const imageOnlySlider = cfg.storefront_theme === "perfecto" || cfg.storefront_theme === "boxy";
  const overlaySlider = cfg.storefront_theme === "trendy" || cfg.storefront_theme === "medico" || cfg.storefront_theme === "mixy" || cfg.storefront_theme === "various" || cfg.storefront_theme === "beast" || cfg.storefront_theme === "locally" || cfg.storefront_theme === "quicksto" || cfg.storefront_theme === "bigshow" || cfg.storefront_theme === "furnis" || cfg.storefront_theme === "carparts" || cfg.storefront_theme === "cosmos" || cfg.storefront_theme === "ondemand";
  const save = async (section, endpoint) => {
    try {
      let payload = cfg[section];
      if (section === "branding") {
        payload = { ...payload, accent_color: payload.accent_color || "#FFDC2E" };
      }
      await api.put(`/merchant/config/${endpoint}`, payload);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">{t("settings.title")}</h1>
        <p className="text-slate-500 text-sm mt-1">{t("settings.subtitle")}</p>
        {cfg.single_product_active && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3" data-testid="settings-sp-lock">
            Homepage is controlled by <Link className="underline font-medium" to="/admin/addons/single-product">Single Product Switch</Link>. Branding, top bar, and footer still apply.
          </p>
        )}
      </div>

      <ProductLanguageHelper variant="settings" language={(i18n.language || "en").split("-")[0]} tutorial={tutorial} />

      <Tabs value={settingsTab} onValueChange={setSettingsTab}>
        <SettingsTabSlider>
          <TabsTrigger value="store_info" data-testid="tab-store-info">{t("settings.tabStoreInfo")}</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">{t("settings.tabBranding")}</TabsTrigger>
          {!cfg.single_product_active && (
            <TabsTrigger value="homepage" data-testid="tab-homepage">{t("settings.tabHomepage")}</TabsTrigger>
          )}
          <TabsTrigger value="topbar" data-testid="tab-topbar">{t("settings.tabTopbar")}</TabsTrigger>
          <TabsTrigger value="menu" data-testid="tab-menu">{t("settings.tabMenu")}</TabsTrigger>
          <TabsTrigger value="footer" data-testid="tab-footer">{t("settings.tabFooter")}</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment">{t("settings.tabPayment")}</TabsTrigger>
          <TabsTrigger value="shipping" data-testid="tab-shipping">{t("settings.tabShipping")}</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tab-tax">Tax</TabsTrigger>
          <TabsTrigger value="storefront" data-testid="tab-storefront">{t("settings.tabStorefront")}</TabsTrigger>
          <TabsTrigger value="product_settings" data-testid="tab-product-settings">{t("settings.tabProductSettings")}</TabsTrigger>
          <TabsTrigger value="under_construction" data-testid="tab-under-construction">{t("settings.tabUnderConstruction")}</TabsTrigger>
          <TabsTrigger value="adult_control" data-testid="tab-adult-control">Adult Control</TabsTrigger>
          <TabsTrigger value="language" data-testid="tab-language">{t("settings.languageTab")}</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">{t("settings.tabEmail")}</TabsTrigger>
          <TabsTrigger value="domain" data-testid="tab-domain">{t("settings.tabDomain")}</TabsTrigger>
        </SettingsTabSlider>

        <TabsContent value="language">
          <Card className="p-6 space-y-5 border-slate-200/70" data-testid="settings-language">
            <p className="text-sm text-slate-600">{t("products.typeHint")}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>{t("settings.dashboardLanguage")}</Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">{t("settings.dashboardHint")}</p>
                <select
                  className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="i18n-dashboard-lang"
                  value={(cfg.i18n || {}).dashboard_language || "en"}
                  onChange={(e) => setPart("i18n", { ...(cfg.i18n || {}), dashboard_language: e.target.value })}
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.native} — {l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>{t("settings.storefrontLanguage")}</Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  {(cfg.i18n || {}).google_storefront ? t("settings.storefrontLocked") : t("settings.storefrontHint")}
                </p>
                <select
                  className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                  data-testid="i18n-storefront-lang"
                  disabled={Boolean((cfg.i18n || {}).google_storefront)}
                  value={(cfg.i18n || {}).google_storefront ? "en" : ((cfg.i18n || {}).storefront_language || "en")}
                  onChange={(e) => setPart("i18n", { ...(cfg.i18n || {}), storefront_language: e.target.value })}
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.native} — {l.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="i18n-google-box">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="i18n-google-toggle">{t("settings.googleStorefront")}</Label>
                  <p className="text-xs text-slate-500 mt-1">{t("settings.googleStorefrontHint")}</p>
                </div>
                <Switch
                  id="i18n-google-toggle"
                  checked={Boolean((cfg.i18n || {}).google_storefront)}
                  data-testid="i18n-google-toggle"
                  onCheckedChange={(v) => {
                    const cur = cfg.i18n || {};
                    const dash = cur.dashboard_language || "en";
                    const nextGoogleLang = cur.google_storefront_language && cur.google_storefront_language !== "en"
                      ? cur.google_storefront_language
                      : (dash !== "en" ? dash : "hi");
                    setPart("i18n", {
                      ...cur,
                      google_storefront: v,
                      storefront_language: v ? "en" : (cur.storefront_language || "en"),
                      google_storefront_language: nextGoogleLang,
                    });
                  }}
                />
              </div>
              {(cfg.i18n || {}).google_storefront ? (
                <div>
                  <Label>{t("settings.googleTargetLanguage")}</Label>
                  <p className="text-xs text-slate-500 mt-1 mb-2">{t("settings.googleTargetHint")}</p>
                  <select
                    className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    data-testid="i18n-google-lang"
                    value={(cfg.i18n || {}).google_storefront_language || "hi"}
                    onChange={(e) => setPart("i18n", { ...(cfg.i18n || {}), google_storefront_language: e.target.value, storefront_language: "en" })}
                  >
                    {LANGUAGES.filter((l) => l.code !== "en").map((l) => (
                      <option key={l.code} value={l.code}>{l.native} — {l.name}</option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
            <Button
              onClick={async () => {
                try {
                  const { data } = await api.put("/merchant/config/i18n", cfg.i18n || {});
                  toast.success(t("common.saved"));
                  window.dispatchEvent(new CustomEvent("dashboard-lang", { detail: data.dashboard_language || "en" }));
                } catch (e) { toast.error(extractErr(e)); }
              }}
              data-testid="i18n-save"
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-1" /> {t("settings.saveLanguage")}
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="store_info">
          <Card className="p-6 space-y-4 border-slate-200/70">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t("settings.storeName")}</Label><Input data-testid="si-name" className="mt-1" value={cfg.store_info.name} onChange={(e) => setPart("store_info", { ...cfg.store_info, name: e.target.value })} /></div>
              <div><Label>{t("common.email")}</Label><Input data-testid="si-email" className="mt-1" value={cfg.store_info.email} onChange={(e) => setPart("store_info", { ...cfg.store_info, email: e.target.value })} /></div>
              <div><Label>{t("common.phone")}</Label><Input data-testid="si-phone" className="mt-1" value={cfg.store_info.phone} onChange={(e) => setPart("store_info", { ...cfg.store_info, phone: e.target.value })} /></div>
              <div><Label>{t("common.whatsapp")}</Label><Input data-testid="si-whatsapp" className="mt-1" value={cfg.store_info.whatsapp} onChange={(e) => setPart("store_info", { ...cfg.store_info, whatsapp: e.target.value })} /></div>
              <div className="col-span-2"><Label>{t("common.address")}</Label><Textarea data-testid="si-address" className="mt-1" value={cfg.store_info.address} onChange={(e) => setPart("store_info", { ...cfg.store_info, address: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Page background color</Label>
                <p className="text-xs text-slate-500 mt-1">Used on every theme, the homepage, and inner pages such as product, cart, and account. Leave blank to keep the theme background.</p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <input
                    type="color"
                    aria-label="Page background color"
                    data-testid="si-page-bg"
                    className="h-10 w-14 rounded border border-slate-200 bg-white p-1"
                    value={/^#[0-9A-Fa-f]{6}$/.test(cfg.store_info.page_bg_color || "") ? cfg.store_info.page_bg_color : "#ffffff"}
                    onChange={(e) => setPart("store_info", { ...cfg.store_info, page_bg_color: e.target.value })}
                  />
                  <Input
                    data-testid="si-page-bg-hex"
                    className="font-mono-nums max-w-[140px]"
                    value={cfg.store_info.page_bg_color || ""}
                    placeholder="#F8F5F0"
                    onChange={(e) => setPart("store_info", { ...cfg.store_info, page_bg_color: e.target.value })}
                  />
                  <Button type="button" variant="outline" data-testid="si-page-bg-clear" onClick={() => setPart("store_info", { ...cfg.store_info, page_bg_color: "" })}>Theme default</Button>
                  {/^#[0-9A-Fa-f]{6}$/.test(cfg.store_info.page_bg_color || "") && (
                    <span className="h-10 w-16 rounded border border-slate-200" style={{ backgroundColor: cfg.store_info.page_bg_color }} data-testid="si-page-bg-preview" />
                  )}
                </div>
              </div>
              <div className="col-span-2 flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-3">
                <div>
                  <Label>Order Cancellation</Label>
                  <p className="text-xs text-slate-500 mt-1">When this is on, a customer can cancel an order and give a reason. The reason appears under Orders → Cancelled by customer, where you approve or disapprove it.</p>
                </div>
                <Switch
                  checked={Boolean(cfg.store_info.order_cancellation)}
                  onCheckedChange={(v) => setPart("store_info", { ...cfg.store_info, order_cancellation: v })}
                  data-testid="si-order-cancellation"
                />
              </div>
              <div className="col-span-2">
                <Label>Site preloader</Label>
                <p className="text-xs text-slate-500 mt-1">Shown while the storefront opens. Pick one of the five, or keep no preloader.</p>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {PRELOADERS.map((p) => {
                    const selected = normalizePreloader(cfg.store_info.preloader) === p.id;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        data-testid={`si-preloader-${p.id}`}
                        onClick={() => setPart("store_info", { ...cfg.store_info, preloader: p.id })}
                        className={`rounded-xl border p-3 text-left transition ${selected ? "border-blue-600 ring-2 ring-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                      >
                        <SitePreloader id={p.id} preview />
                        <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{p.blurb}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div>
              <Label>{t("common.logo")}</Label>
              <div className="mt-1"><UploadImage kind="logo" testid="si-logo-upload" hint="Recommended 800×240 px (wide logo). Shown at about 48–64px tall. On BigShow, Furnis and Cosmos this is the overlay (transparent header) logo." value={cfg.store_info.logo_url} onChange={(url) => setPart("store_info", { ...cfg.store_info, logo_url: url })} /></div>
              <p className="text-xs text-slate-500 mt-2">{t("settings.logoHint")}</p>
              <div className="mt-4">
                <Label>{t("settings.logoWhiteBg")}</Label>
                <div className="mt-1"><UploadImage kind="logo" testid="si-logo-white-bg-upload" hint="Used on BigShow, Furnis and Cosmos after you scroll (and on inner pages), when the header turns white. Upload a dark logo that reads on a light background." value={cfg.store_info.logo_white_bg_url} onChange={(url) => setPart("store_info", { ...cfg.store_info, logo_white_bg_url: url })} /></div>
                <p className="text-xs text-slate-500 mt-2">{t("settings.logoWhiteBgHint")}</p>
                {cfg.store_info.logo_white_bg_url && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 w-fit max-w-full">
                    <img src={mediaUrl(cfg.store_info.logo_white_bg_url)} alt="" className="h-11 w-auto max-w-[200px] object-contain" data-testid="si-logo-white-bg-preview" />
                  </div>
                )}
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>{t("settings.mobileSize")}</Label>
                    <span className="text-xs font-mono-nums text-slate-500" data-testid="si-logo-h-mobile-val">{Number(cfg.store_info.logo_height_mobile) || 48}px</span>
                  </div>
                  <Slider
                    min={24}
                    max={96}
                    step={2}
                    value={[Number(cfg.store_info.logo_height_mobile) || 48]}
                    onValueChange={(v) => setPart("store_info", { ...cfg.store_info, logo_height_mobile: v[0] })}
                    data-testid="si-logo-h-mobile"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>{t("settings.desktopSize")}</Label>
                    <span className="text-xs font-mono-nums text-slate-500" data-testid="si-logo-h-desktop-val">{Number(cfg.store_info.logo_height_desktop) || 64}px</span>
                  </div>
                  <Slider
                    min={24}
                    max={96}
                    step={2}
                    value={[Number(cfg.store_info.logo_height_desktop) || 64]}
                    onValueChange={(v) => setPart("store_info", { ...cfg.store_info, logo_height_desktop: v[0] })}
                    data-testid="si-logo-h-desktop"
                  />
                </div>
              </div>
              {cfg.store_info.logo_url && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{t("settings.mobilePreview")}</div>
                    <img src={mediaUrl(cfg.store_info.logo_url)} alt="" style={{ height: Number(cfg.store_info.logo_height_mobile) || 48 }} className="w-auto max-w-full object-contain" data-testid="si-logo-preview-mobile" />
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{t("settings.desktopPreview")}</div>
                    <img src={mediaUrl(cfg.store_info.logo_url)} alt="" style={{ height: Number(cfg.store_info.logo_height_desktop) || 64 }} className="w-auto max-w-full object-contain" data-testid="si-logo-preview-desktop" />
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label>{t("settings.footerLogo")}</Label>
              <div className="mt-1"><UploadImage kind="logo" testid="si-footer-logo-upload" hint="Recommended 800×240 px. Shown in the footer without any color filter." value={cfg.store_info.footer_logo_url} onChange={(url) => setPart("store_info", { ...cfg.store_info, footer_logo_url: url })} /></div>
              <p className="text-xs text-slate-500 mt-2">{t("settings.footerLogoHint")}</p>
              {cfg.store_info.footer_logo_url && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-neutral-900 p-3 w-fit max-w-full">
                  <img src={mediaUrl(cfg.store_info.footer_logo_url)} alt="" className="h-11 w-auto max-w-[200px] object-contain" data-testid="si-footer-logo-preview" />
                </div>
              )}
            </div>
            <Button onClick={() => save("store_info", "store_info")} data-testid="si-save" className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> {t("settings.saveStoreInfo")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="branding">
          <Card className="p-6 space-y-4 border-slate-200/70">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("settings.primaryColor")}</Label>
                <div className="flex gap-2 mt-1"><Input type="color" data-testid="br-primary" className="w-16 p-1 h-10" value={cfg.branding.primary_color} onChange={(e) => setPart("branding", { ...cfg.branding, primary_color: e.target.value })} /><Input className="font-mono-nums" value={cfg.branding.primary_color} onChange={(e) => setPart("branding", { ...cfg.branding, primary_color: e.target.value })} /></div>
              </div>
              <div>
                <Label>{t("settings.secondaryColor")}</Label>
                <div className="flex gap-2 mt-1"><Input type="color" data-testid="br-secondary" className="w-16 p-1 h-10" value={cfg.branding.secondary_color} onChange={(e) => setPart("branding", { ...cfg.branding, secondary_color: e.target.value })} /><Input className="font-mono-nums" value={cfg.branding.secondary_color} onChange={(e) => setPart("branding", { ...cfg.branding, secondary_color: e.target.value })} /></div>
              </div>
              <div>
                <Label>{cfg.storefront_theme === "mixy" ? "Mixy accent (yellow)" : "Accent color"}</Label>
                <p className="text-xs text-slate-500 mt-0.5">On Mixy this is the Sale tag, hero button, and search accent. Default #FFDC2E.</p>
                <div className="flex gap-2 mt-1"><Input type="color" data-testid="br-accent" className="w-16 p-1 h-10" value={cfg.branding.accent_color || "#FFDC2E"} onChange={(e) => setPart("branding", { ...cfg.branding, accent_color: e.target.value })} /><Input className="font-mono-nums" value={cfg.branding.accent_color || "#FFDC2E"} onChange={(e) => setPart("branding", { ...cfg.branding, accent_color: e.target.value })} /></div>
              </div>
            </div>
            <div>
              <Label>{t("settings.favicon")}</Label>
              <div className="mt-1"><UploadImage kind="favicon" testid="br-favicon-upload" hint="Recommended 512×512 px (square PNG). 32×32 also works." value={cfg.branding.favicon_url} onChange={(url) => setPart("branding", { ...cfg.branding, favicon_url: url })} /></div>
            </div>
            <Button onClick={() => save("branding", "branding")} data-testid="br-save" className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> {t("settings.saveBranding")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="homepage">
          {cfg.single_product_active ? (
            <Card className="p-6 space-y-3 border-amber-200 bg-amber-50" data-testid="homepage-locked">
              <p className="text-sm text-amber-900">Homepage is locked while <strong>Single Product Switch</strong> is on. Header, branding, top bar, and footer still work as usual.</p>
              <Link to="/admin/addons/single-product" className="text-sm font-medium text-violet-700 underline">Configure landing page</Link>
            </Card>
          ) : (
          <Card className="p-6 space-y-5 border-slate-200/70">
            {(!cfg.homepage.slides || cfg.homepage.slides.length === 0) && (
              <>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Main hero <span className="normal-case tracking-normal text-slate-400 font-normal">— used when no slides are set below</span></div>
                </div>
                <div><Label>{t("settings.heroImage")} <span className="text-slate-400 text-xs font-normal">({t("settings.desktop")})</span></Label><div className="mt-1"><UploadImage kind="hero" testid="hp-hero-upload" hint={imageOnlySlider ? `${cfg.storefront_theme === "boxy" ? "Boxy" : "Perfecto"} desktop slider: exactly 1920 × 500 px (wide). Full-bleed graphic only — the image is clickable via CTA link on slides.` : overlaySlider ? "Trendy / Medico / Mixy / Various / Beast desktop slider: exactly 1920 × 500 px (wide). Title, subtitle and CTA sit in white on a left-to-right black shade only when that copy is set. A CTA link alone makes the full banner clickable with no shade. Mixy sits inset with rounded corners." : "Recommended 1920×800 px. Scales on all screens."} value={cfg.homepage.hero_image_url} onChange={(url) => setPart("homepage", { ...cfg.homepage, hero_image_url: url })} /></div></div>
                <div><Label>{t("settings.heroImage")} <span className="text-slate-400 text-xs font-normal">({t("settings.mobileOptional")})</span></Label><div className="mt-1"><UploadImage kind="hero" testid="hp-hero-upload-mobile" hint={imageOnlySlider ? `${cfg.storefront_theme === "boxy" ? "Boxy" : "Perfecto"} mobile slider: exactly 1080 × 1350 px (portrait 4:5). Optional but recommended so phones are not cropped.` : overlaySlider ? "Trendy / Medico mobile slider: exactly 1080 × 1350 px (portrait 4:5). Optional but recommended." : "Recommended 800×1000 px (portrait). Optional."} value={cfg.homepage.hero_image_url_mobile} onChange={(url) => setPart("homepage", { ...cfg.homepage, hero_image_url_mobile: url })} /></div></div>
                {!imageOnlySlider ? (
                  <>
                    <div><Label>{t("settings.heroTitle")}</Label><Input data-testid="hp-title" className="mt-1" value={cfg.homepage.hero_title} onChange={(e) => setPart("homepage", { ...cfg.homepage, hero_title: e.target.value })} /></div>
                    <div><Label>{t("settings.heroSubtitle")}</Label><Textarea data-testid="hp-subtitle" className="mt-1" value={cfg.homepage.hero_subtitle} onChange={(e) => setPart("homepage", { ...cfg.homepage, hero_subtitle: e.target.value })} /></div>
                    <div><Label>{t("settings.ctaText")}</Label><Input data-testid="hp-cta" className="mt-1" value={cfg.homepage.hero_cta_text} onChange={(e) => setPart("homepage", { ...cfg.homepage, hero_cta_text: e.target.value })} /></div>
                  </>
                ) : null}
              </>
            )}
            {cfg.homepage.slides && cfg.homepage.slides.length > 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>The <strong>hero slider</strong> below is active. The Main hero fields are hidden while slides exist — remove all slides to fall back to the single Main hero.</div>
              </div>
            )}

            <div className={(!cfg.homepage.slides || cfg.homepage.slides.length === 0) ? "pt-4 border-t border-slate-100" : ""}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Hero slider · slides</div>
                  {imageOnlySlider ? (
                    <p className="text-xs text-slate-500 mt-1">{cfg.storefront_theme === "boxy" ? "Boxy" : "Perfecto"} hides title, subtitle and the CTA button. The <strong>CTA link</strong> still works — the full slide image is clickable. Desktop <strong>1920 × 500 px</strong> · Mobile <strong>1080 × 1350 px</strong>.</p>
                  ) : overlaySlider ? (
                    <p className="text-xs text-slate-500 mt-1">Title, subtitle and CTA sit in white on a left-to-right black shade (same as Classic) only when that text is set. Leave them empty and set a CTA link to make the full banner clickable with no shade. Desktop <strong>1920 × 500 px</strong> · Mobile <strong>1080 × 1350 px</strong>.</p>
                  ) : null}
                  <p className="text-xs text-slate-500 mt-1">Drag <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold"><GripVertical className="w-3 h-3" />handle</span> to reorder. Slides rotate automatically on the storefront.</p>
                </div>
                <Button type="button" variant="outline" data-testid="hp-add-slide" onClick={() => {
                  const slides = [...(cfg.homepage.slides || [])];
                  slides.push({ image_url: null, title: "", subtitle: "", cta_text: "Shop Now", cta_link: "" });
                  setPart("homepage", { ...cfg.homepage, slides });
                }}><Plus className="w-4 h-4 mr-1" /> Add slide</Button>
              </div>
              {(cfg.homepage.slides || []).length === 0 ? (
                <div className="p-6 rounded-lg border border-dashed border-slate-300 text-center text-sm text-slate-500">No slides yet. Click <span className="font-semibold">Add slide</span> to create a rotating carousel.</div>
              ) : (
                <div className="space-y-3">
                  {(cfg.homepage.slides || []).map((sl, i) => (
                    <div
                      key={i}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); setDragIdx(i); }}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverIdx(i); }}
                      onDragLeave={() => setDragOverIdx((v) => (v === i ? null : v))}
                      onDrop={(e) => {
                        e.preventDefault();
                        const from = parseInt(e.dataTransfer.getData("text/plain") || String(dragIdx ?? -1), 10);
                        if (Number.isNaN(from) || from === i) { setDragIdx(null); setDragOverIdx(null); return; }
                        const slides = [...cfg.homepage.slides];
                        const [moved] = slides.splice(from, 1);
                        slides.splice(i, 0, moved);
                        setPart("homepage", { ...cfg.homepage, slides });
                        setDragIdx(null); setDragOverIdx(null);
                      }}
                      onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                      className={`p-4 rounded-lg border bg-slate-50/50 space-y-3 transition-all ${dragOverIdx === i && dragIdx !== i ? "border-blue-500 ring-2 ring-blue-100" : "border-slate-200"} ${dragIdx === i ? "opacity-50" : ""}`}
                      data-testid={`hp-slide-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700" title="Drag to reorder" data-testid={`hp-slide-handle-${i}`}><GripVertical className="w-4 h-4" /></span>
                          <div className="text-xs font-semibold text-slate-700 font-mono-nums">Slide #{i + 1}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button type="button" variant="ghost" size="sm" disabled={i === 0} data-testid={`hp-slide-up-${i}`} onClick={() => {
                            const slides = [...cfg.homepage.slides];
                            [slides[i - 1], slides[i]] = [slides[i], slides[i - 1]];
                            setPart("homepage", { ...cfg.homepage, slides });
                          }}><ChevronUp className="w-4 h-4" /></Button>
                          <Button type="button" variant="ghost" size="sm" disabled={i === (cfg.homepage.slides.length - 1)} data-testid={`hp-slide-down-${i}`} onClick={() => {
                            const slides = [...cfg.homepage.slides];
                            [slides[i + 1], slides[i]] = [slides[i], slides[i + 1]];
                            setPart("homepage", { ...cfg.homepage, slides });
                          }}><ChevronDown className="w-4 h-4" /></Button>
                          <Button type="button" variant="ghost" size="sm" data-testid={`hp-slide-remove-${i}`} onClick={() => {
                            const slides = cfg.homepage.slides.filter((_, k) => k !== i);
                            setPart("homepage", { ...cfg.homepage, slides });
                          }} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Slide image <span className="text-slate-400 font-normal">(desktop)</span></Label>
                        <div className="mt-1">
                          <UploadImage kind="hero" testid={`hp-slide-image-${i}`} hint={imageOnlySlider ? `${cfg.storefront_theme === "boxy" ? "Boxy" : "Perfecto"} desktop: exactly 1920 × 500 px. No overlay — the full image uses the CTA link.` : overlaySlider ? "Trendy / Medico desktop: exactly 1920 × 500 px. White title, subtitle and CTA on a left-to-right black shade only when copy is set; otherwise the CTA link makes the full banner clickable." : "Recommended 1920×800 px."} value={sl.image_url} onChange={(url) => {
                            const slides = [...cfg.homepage.slides]; slides[i] = { ...sl, image_url: url };
                            setPart("homepage", { ...cfg.homepage, slides });
                          }} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Slide image <span className="text-slate-400 font-normal">(mobile — optional)</span></Label>
                        <div className="mt-1">
                          <UploadImage kind="hero" testid={`hp-slide-image-mobile-${i}`} hint={imageOnlySlider ? `${cfg.storefront_theme === "boxy" ? "Boxy" : "Perfecto"} mobile: exactly 1080 × 1350 px (portrait 4:5). Optional but recommended.` : overlaySlider ? "Trendy / Medico mobile: exactly 1080 × 1350 px (portrait 4:5). Optional but recommended." : "Recommended 800×1000 px (portrait). Optional."} value={sl.image_url_mobile} onChange={(url) => {
                            const slides = [...cfg.homepage.slides]; slides[i] = { ...sl, image_url_mobile: url };
                            setPart("homepage", { ...cfg.homepage, slides });
                          }} />
                        </div>
                      </div>
                      {!imageOnlySlider ? (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Title</Label>
                              <Input data-testid={`hp-slide-title-${i}`} className="mt-1" value={sl.title || ""} onChange={(e) => {
                                const slides = [...cfg.homepage.slides]; slides[i] = { ...sl, title: e.target.value };
                                setPart("homepage", { ...cfg.homepage, slides });
                              }} />
                            </div>
                            <div>
                              <Label className="text-xs">CTA text</Label>
                              <Input data-testid={`hp-slide-cta-${i}`} className="mt-1" value={sl.cta_text || ""} onChange={(e) => {
                                const slides = [...cfg.homepage.slides]; slides[i] = { ...sl, cta_text: e.target.value };
                                setPart("homepage", { ...cfg.homepage, slides });
                              }} />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Subtitle</Label>
                            <Textarea rows={2} data-testid={`hp-slide-subtitle-${i}`} className="mt-1" value={sl.subtitle || ""} onChange={(e) => {
                              const slides = [...cfg.homepage.slides]; slides[i] = { ...sl, subtitle: e.target.value };
                              setPart("homepage", { ...cfg.homepage, slides });
                            }} />
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-slate-500">Title, subtitle and CTA button are hidden on Perfecto. The CTA link below makes the whole slide image clickable.</p>
                      )}
                      <div>
                        <Label className="text-xs">CTA link <span className="text-slate-400 font-normal">(e.g. #shop, /{'{slug}'}/category/{'{cat}'})</span></Label>
                        <Input data-testid={`hp-slide-link-${i}`} className="mt-1 font-mono-nums" value={sl.cta_link || ""} placeholder="#shop" onChange={(e) => {
                          const slides = [...cfg.homepage.slides]; slides[i] = { ...sl, cta_link: e.target.value };
                          setPart("homepage", { ...cfg.homepage, slides });
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Featured banners (two side-by-side)</div>
              <p className="text-xs text-slate-500 mb-3">Two promotional cards shown side by side. Each has an image, heading, subtext and a CTA button.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {[0, 1].map((bi) => {
                  const list = cfg.homepage.featured_banners && cfg.homepage.featured_banners.length ? cfg.homepage.featured_banners : (cfg.homepage.featured_banner ? [cfg.homepage.featured_banner] : []);
                  const b = list[bi] || {};
                  const setB = (patch) => {
                    const next = [...(list)];
                    while (next.length < 2) next.push({});
                    next[bi] = { ...next[bi], ...patch };
                    setPart("homepage", { ...cfg.homepage, featured_banners: next });
                  };
                  return (
                    <div key={bi} className="p-4 rounded-lg border border-slate-200 bg-slate-50/50 space-y-3" data-testid={`hp-fb-${bi}`}>
                      <div className="text-xs font-semibold text-slate-700">Banner #{bi + 1}</div>
                      <div><Label className="text-xs">Image</Label><div className="mt-1"><UploadImage kind="hero" testid={`hp-fb-image-${bi}`} hint="Recommended 600×400 px (3:2)." value={b.image_url} onChange={(url) => setB({ image_url: url })} /></div></div>
                      <div><Label className="text-xs">Title</Label><Input data-testid={`hp-fb-title-${bi}`} className="mt-1" value={b.title || ""} onChange={(e) => setB({ title: e.target.value })} /></div>
                      <div><Label className="text-xs">Subtitle</Label><Input data-testid={`hp-fb-subtitle-${bi}`} className="mt-1" value={b.subtitle || ""} onChange={(e) => setB({ subtitle: e.target.value })} /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">CTA text</Label><Input data-testid={`hp-fb-cta-${bi}`} className="mt-1" value={b.cta_text || ""} onChange={(e) => setB({ cta_text: e.target.value })} placeholder="Shop now" /></div>
                        <div><Label className="text-xs">CTA link</Label><Input data-testid={`hp-fb-link-${bi}`} className="mt-1 font-mono-nums" value={b.cta_link || ""} onChange={(e) => setB({ cta_link: e.target.value })} placeholder="#shop" /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Promo banner (offer / campaign)</div>
              <p className="text-xs text-slate-500 mb-3">Upload a single wide banner you designed yourself. It shows full-width and links wherever you choose. Add a separate mobile image for a better fit on phones.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Banner image <span className="text-slate-400 text-xs font-normal">(desktop, wide)</span></Label><div className="mt-1"><UploadImage kind="hero" testid="hp-pb-image" hint="Recommended 1920×400 px (wide banner)." value={cfg.homepage.promo_banner?.image_url} onChange={(url) => setPart("homepage", { ...cfg.homepage, promo_banner: { ...(cfg.homepage.promo_banner || {}), image_url: url } })} /></div></div>
                <div><Label>Banner image <span className="text-slate-400 text-xs font-normal">(mobile — optional)</span></Label><div className="mt-1"><UploadImage kind="hero" testid="hp-pb-image-mobile" hint="Recommended 800×500 px. Optional." value={cfg.homepage.promo_banner?.image_url_mobile} onChange={(url) => setPart("homepage", { ...cfg.homepage, promo_banner: { ...(cfg.homepage.promo_banner || {}), image_url_mobile: url } })} /></div></div>
              </div>
              <div className="mt-3"><Label>Click link <span className="text-slate-400 text-xs font-normal">(optional)</span></Label><Input data-testid="hp-pb-link" className="mt-1 font-mono-nums" value={cfg.homepage.promo_banner?.cta_link || ""} onChange={(e) => setPart("homepage", { ...cfg.homepage, promo_banner: { ...(cfg.homepage.promo_banner || {}), cta_link: e.target.value } })} placeholder="#shop or /fresh-fruits/shop?category=..." /></div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Testimonials</div>
                <div className="flex items-center gap-2"><Label className="text-xs">Show section</Label><Switch checked={cfg.homepage.show_testimonials !== false} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_testimonials: v })} data-testid="hp-show-testi" /></div>
              </div>
              <p className="text-xs text-slate-500 mb-3">Add customer reviews shown as a rotating slider on the homepage.</p>
              <div className="space-y-3">
                {(cfg.homepage.testimonials || []).map((t, i) => (
                  <div key={i} className="p-4 rounded-lg border border-slate-200 bg-slate-50/50 space-y-3" data-testid={`hp-testi-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold text-slate-700">Testimonial #{i + 1}</div>
                      <button type="button" className="text-red-600 text-xs" data-testid={`hp-testi-remove-${i}`} onClick={() => { const arr = [...cfg.homepage.testimonials]; arr.splice(i, 1); setPart("homepage", { ...cfg.homepage, testimonials: arr }); }}>Remove</button>
                    </div>
                    <div className="grid md:grid-cols-[100px_1fr] gap-3">
                      <div><Label className="text-xs">Photo</Label><div className="mt-1"><UploadImage kind="testimonial" testid={`hp-testi-img-${i}`} hint="Recommended 400×400 px (square)." value={t.image_url} onChange={(url) => { const arr = [...cfg.homepage.testimonials]; arr[i] = { ...t, image_url: url }; setPart("homepage", { ...cfg.homepage, testimonials: arr }); }} /></div></div>
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div><Label className="text-xs">Name / title</Label><Input data-testid={`hp-testi-name-${i}`} className="mt-1" value={t.name || ""} onChange={(e) => { const arr = [...cfg.homepage.testimonials]; arr[i] = { ...t, name: e.target.value }; setPart("homepage", { ...cfg.homepage, testimonials: arr }); }} /></div>
                          <div><Label className="text-xs">Rating (1–5)</Label><Input type="number" min="1" max="5" data-testid={`hp-testi-rating-${i}`} className="mt-1 font-mono-nums" value={t.rating || 5} onChange={(e) => { const arr = [...cfg.homepage.testimonials]; arr[i] = { ...t, rating: Number(e.target.value) }; setPart("homepage", { ...cfg.homepage, testimonials: arr }); }} /></div>
                        </div>
                        <div><Label className="text-xs">Review</Label><Textarea data-testid={`hp-testi-review-${i}`} rows={2} className="mt-1" value={t.review || ""} onChange={(e) => { const arr = [...cfg.homepage.testimonials]; arr[i] = { ...t, review: e.target.value }; setPart("homepage", { ...cfg.homepage, testimonials: arr }); }} /></div>
                      </div>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" data-testid="hp-testi-add" onClick={() => setPart("homepage", { ...cfg.homepage, testimonials: [...(cfg.homepage.testimonials || []), { name: "", review: "", rating: 5, image_url: "" }] })}>+ Add testimonial</Button>
              </div>
            </div>

            {cfg.storefront_theme === "cosmos" ? (
            <div className="pt-4 border-t border-slate-100" data-testid="hp-newsletter-bg">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Newsletter background</div>
              <p className="text-xs text-slate-500 mb-3">Cosmos homepage newsletter strip. Upload a wide photo for desktop and an optional portrait crop for phones.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Newsletter image <span className="text-slate-400 text-xs font-normal">(desktop)</span></Label><div className="mt-1"><UploadImage kind="hero" testid="hp-newsletter-upload" hint="Recommended 1920×600 px." value={cfg.homepage.newsletter_image_url} onChange={(url) => setPart("homepage", { ...cfg.homepage, newsletter_image_url: url })} /></div></div>
                <div><Label>Newsletter image <span className="text-slate-400 text-xs font-normal">(mobile — optional)</span></Label><div className="mt-1"><UploadImage kind="hero" testid="hp-newsletter-upload-mobile" hint="Recommended 1080×900 px. Optional." value={cfg.homepage.newsletter_image_url_mobile} onChange={(url) => setPart("homepage", { ...cfg.homepage, newsletter_image_url_mobile: url })} /></div></div>
              </div>
            </div>
            ) : null}

            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Why choose us</div>
                <div className="flex items-center gap-2"><Label className="text-xs">Show section</Label><Switch checked={cfg.homepage.show_why_us !== false} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_why_us: v })} data-testid="hp-show-whyus" /></div>
              </div>
              <p className="text-xs text-slate-500 mb-3">Heading, tile text and icons (Lucide or emoji) shown near the footer on the homepage.</p>
              <div className="grid md:grid-cols-2 gap-3 mb-3">
                <div><Label className="text-xs">Kicker</Label><Input data-testid="hp-whyus-heading" className="mt-1" value={cfg.homepage.why_us_heading || ""} onChange={(e) => setPart("homepage", { ...cfg.homepage, why_us_heading: e.target.value })} placeholder="Why choose us" /></div>
                <div><Label className="text-xs">Heading</Label><Input data-testid="hp-whyus-subheading" className="mt-1" value={cfg.homepage.why_us_subheading || ""} onChange={(e) => setPart("homepage", { ...cfg.homepage, why_us_subheading: e.target.value })} placeholder="Loved by shoppers, backed by trust." /></div>
              </div>
              <div className="space-y-3">
                {(cfg.homepage.why_us || []).map((w, i) => (
                  <div key={i} className="p-4 rounded-lg border border-slate-200 bg-slate-50/50 space-y-3" data-testid={`hp-whyus-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold text-slate-700 inline-flex items-center gap-2">
                        <WhyUsIconPreview name={w.icon} /> Tile #{i + 1}
                      </div>
                      <button type="button" className="text-red-600 text-xs" data-testid={`hp-whyus-remove-${i}`} onClick={() => { const arr = [...cfg.homepage.why_us]; arr.splice(i, 1); setPart("homepage", { ...cfg.homepage, why_us: arr }); }}>Remove</button>
                    </div>
                    <WhyUsIconPicker value={w.icon} testId={`hp-whyus-icons-${i}`} onChange={(icon) => { const arr = [...cfg.homepage.why_us]; arr[i] = { ...w, icon }; setPart("homepage", { ...cfg.homepage, why_us: arr }); }} />
                    <div className="grid md:grid-cols-2 gap-2">
                      <div><Label className="text-xs">Title</Label><Input data-testid={`hp-whyus-title-${i}`} className="mt-1" value={w.title || ""} onChange={(e) => { const arr = [...cfg.homepage.why_us]; arr[i] = { ...w, title: e.target.value }; setPart("homepage", { ...cfg.homepage, why_us: arr }); }} /></div>
                      <div><Label className="text-xs">Description</Label><Input data-testid={`hp-whyus-desc-${i}`} className="mt-1" value={w.description || ""} onChange={(e) => { const arr = [...cfg.homepage.why_us]; arr[i] = { ...w, description: e.target.value }; setPart("homepage", { ...cfg.homepage, why_us: arr }); }} /></div>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" data-testid="hp-whyus-add" onClick={() => setPart("homepage", { ...cfg.homepage, why_us: [...(cfg.homepage.why_us || []), { icon: "Star", title: "", description: "" }] })}>+ Add tile</Button>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Android app / Play Store</div>
                <div className="flex items-center gap-2"><Label className="text-xs">Show section</Label><Switch checked={!!cfg.homepage.show_android_app} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_android_app: v })} data-testid="hp-show-android" /></div>
              </div>
              <p className="text-xs text-slate-500 mb-3">Shown after Why choose us. Hidden on the storefront until a Play Store URL is saved.</p>
              <div className={`space-y-3 ${cfg.homepage.show_android_app ? "" : "opacity-60"}`}>
                <div><Label>Section heading</Label><Input data-testid="hp-android-heading" className="mt-1" value={cfg.homepage.android_app?.heading || ""} onChange={(e) => setPart("homepage", { ...cfg.homepage, android_app: { ...(cfg.homepage.android_app || {}), heading: e.target.value } })} placeholder="Download Our Android App from Play Store" /></div>
                <div><Label>Subheading</Label><Textarea data-testid="hp-android-subheading" rows={2} className="mt-1" value={cfg.homepage.android_app?.subheading || ""} onChange={(e) => setPart("homepage", { ...cfg.homepage, android_app: { ...(cfg.homepage.android_app || {}), subheading: e.target.value } })} placeholder="Shop faster on your phone…" /></div>
                <div><Label>Google Play URL</Label><Input data-testid="hp-android-url" className="mt-1 font-mono-nums" value={cfg.homepage.android_app?.play_store_url || ""} onChange={(e) => setPart("homepage", { ...cfg.homepage, android_app: { ...(cfg.homepage.android_app || {}), play_store_url: e.target.value } })} placeholder="https://play.google.com/store/apps/details?id=…" /></div>
                <div><Label>Badge label <span className="text-slate-400 text-xs font-normal">(small text above “Google Play”)</span></Label><Input data-testid="hp-android-cta" className="mt-1" value={cfg.homepage.android_app?.cta_text || ""} onChange={(e) => setPart("homepage", { ...cfg.homepage, android_app: { ...(cfg.homepage.android_app || {}), cta_text: e.target.value } })} placeholder="Get it on" /></div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between gap-4 mb-1">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">TikTok Commerce</div>
                  <p className="text-xs text-slate-500 mt-1">Watch & Shop With Us — YouTube videos paired with a product. Upload a still image (shown with a play icon until tap) or an MP4 clip that loops muted on the storefront. Cards are 9:16; stars, title and discount come from the selected product.</p>
                </div>
                <Switch checked={!!cfg.homepage.show_tiktok_commerce} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_tiktok_commerce: v })} data-testid="hp-show-tiktok" />
              </div>
              <div className={`mt-3 space-y-3 ${cfg.homepage.show_tiktok_commerce ? "" : "opacity-60"}`}>
                <div>
                  <Label>Section title</Label>
                  <Input
                    data-testid="hp-tiktok-title"
                    className="mt-1"
                    value={cfg.homepage.tiktok_commerce?.title || ""}
                    onChange={(e) => setPart("homepage", { ...cfg.homepage, tiktok_commerce: { ...(cfg.homepage.tiktok_commerce || {}), title: e.target.value } })}
                    placeholder="Watch & Shop With Us"
                  />
                </div>
                {(cfg.homepage.tiktok_commerce?.items || []).map((it, i) => (
                  <div key={i} className="p-3 rounded-lg border border-slate-200 bg-slate-50/70 space-y-2" data-testid={`hp-tiktok-item-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold text-slate-700">Video #{i + 1}</div>
                      <Button type="button" variant="ghost" size="sm" className="text-red-600" data-testid={`hp-tiktok-remove-${i}`} onClick={() => {
                        const items = (cfg.homepage.tiktok_commerce?.items || []).filter((_, k) => k !== i);
                        setPart("homepage", { ...cfg.homepage, tiktok_commerce: { ...(cfg.homepage.tiktok_commerce || {}), items } });
                      }}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <div>
                      <Label className="text-xs">YouTube URL</Label>
                      <Input
                        data-testid={`hp-tiktok-url-${i}`}
                        className="mt-1 font-mono-nums"
                        value={it.video_url || ""}
                        placeholder="https://www.youtube.com/watch?v=… or /shorts/…"
                        onChange={(e) => {
                          const items = [...(cfg.homepage.tiktok_commerce?.items || [])];
                          items[i] = { ...items[i], video_url: e.target.value };
                          setPart("homepage", { ...cfg.homepage, tiktok_commerce: { ...(cfg.homepage.tiktok_commerce || {}), items } });
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Cover image or MP4 <span className="text-slate-400 font-normal">(fallback)</span></Label>
                      <div className="mt-1">
                        <UploadImage
                          kind="tiktok"
                          testid={`hp-tiktok-cover-${i}`}
                          accept="image/jpeg,image/png,image/webp,image/avif,video/mp4"
                          hint="Still image (JPG/PNG/WebP) shows with a play icon until YouTube loads. MP4 loops muted with no audio. Portrait 9:16, max 20MB for video."
                          value={it.clip_url || it.image_url}
                          onChange={(url) => {
                            const items = [...(cfg.homepage.tiktok_commerce?.items || [])];
                            const mp4 = isMp4Url(url);
                            items[i] = { ...items[i], image_url: mp4 ? "" : url, clip_url: mp4 ? url : "" };
                            setPart("homepage", { ...cfg.homepage, tiktok_commerce: { ...(cfg.homepage.tiktok_commerce || {}), items } });
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Linked product</Label>
                      <select
                        data-testid={`hp-tiktok-product-${i}`}
                        className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                        value={it.product_id || ""}
                        onChange={(e) => {
                          const items = [...(cfg.homepage.tiktok_commerce?.items || [])];
                          items[i] = { ...items[i], product_id: e.target.value };
                          setPart("homepage", { ...cfg.homepage, tiktok_commerce: { ...(cfg.homepage.tiktok_commerce || {}), items } });
                        }}
                      >
                        <option value="">Select a product</option>
                        {hpProducts.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="hp-tiktok-add"
                  onClick={() => {
                    const items = [...(cfg.homepage.tiktok_commerce?.items || []), { video_url: "", product_id: "", image_url: "", clip_url: "" }];
                    setPart("homepage", { ...cfg.homepage, tiktok_commerce: { title: cfg.homepage.tiktok_commerce?.title || "Watch & Shop With Us", items } });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add video
                </Button>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Section toggles</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50"><Label>Categories</Label><Switch checked={cfg.homepage.show_categories} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_categories: v })} data-testid="hp-show-cats" /></div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50"><Label>Featured</Label><Switch checked={cfg.homepage.show_featured} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_featured: v })} data-testid="hp-show-feat" /></div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50"><Label>Popular</Label><Switch checked={cfg.homepage.show_popular !== false} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_popular: v })} data-testid="hp-show-popular" /></div>
                {cfg.storefront_theme === "cosmos" && cfg.cosmos_design_enhancer_active ? (
                  <div className="col-span-2 md:col-span-3 p-3 rounded-lg bg-sky-50 border border-sky-100" data-testid="hp-cosmos-popular-badge">
                    <Label>Popular right now badge</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Replaces “Most Reordered” on featured products in the Cosmos Popular right now section.</p>
                    <Input data-testid="hp-cosmos-popular-badge-input" className="mt-1 max-w-sm" maxLength={40} value={cfg.homepage.cosmos_popular_badge || "Most Reordered"} onChange={(e) => setPart("homepage", { ...cfg.homepage, cosmos_popular_badge: e.target.value })} />
                  </div>
                ) : null}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50"><Label>Why us</Label><Switch checked={cfg.homepage.show_why_us !== false} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_why_us: v })} data-testid="hp-show-whyus-2" /></div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50"><Label>Testimonials</Label><Switch checked={cfg.homepage.show_testimonials !== false} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_testimonials: v })} data-testid="hp-show-testi-2" /></div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50"><Label>Android app</Label><Switch checked={!!cfg.homepage.show_android_app} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_android_app: v })} data-testid="hp-show-android-2" /></div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50"><Label>TikTok Commerce</Label><Switch checked={!!cfg.homepage.show_tiktok_commerce} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_tiktok_commerce: v })} data-testid="hp-show-tiktok-2" /></div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50"><Label>About strip</Label><Switch checked={cfg.homepage.show_about_strip} onCheckedChange={(v) => setPart("homepage", { ...cfg.homepage, show_about_strip: v })} data-testid="hp-show-about" /></div>
              </div>
              <div className="mt-3"><Label>About strip text</Label><p className="text-xs text-slate-500 mt-0.5">A slim tagline band shown right under the hero banner. Separate phrases with a period or • to show them as bullets, e.g. &quot;Free delivery • Fresh daily • Easy returns&quot;.</p><Input data-testid="hp-about-text" className="mt-1" value={cfg.homepage.about_strip_text || ""} onChange={(e) => setPart("homepage", { ...cfg.homepage, about_strip_text: e.target.value })} placeholder="Quality. Value. Trust." /></div>
            </div>

            {cfg.store_blog_entitled && (
              <div className="pt-4 border-t border-slate-100 space-y-3" data-testid="hp-blog-section">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Blog Section</div>
                <p className="text-xs text-slate-500">Shows Latest Stories on every theme homepage. Switch the addon off in App Store to hide /blog as well.</p>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <Label>Show on homepage</Label>
                  <Switch
                    checked={cfg.store_blog?.show_on_homepage !== false}
                    onCheckedChange={(v) => setCfg((c) => ({ ...c, store_blog: { ...(c.store_blog || {}), show_on_homepage: v } }))}
                    data-testid="hp-show-blog"
                  />
                </div>
                <div>
                  <Label>Section heading</Label>
                  <Input
                    data-testid="hp-blog-heading"
                    className="mt-1"
                    value={cfg.store_blog?.heading || ""}
                    onChange={(e) => setCfg((c) => ({ ...c, store_blog: { ...(c.store_blog || {}), heading: e.target.value } }))}
                    placeholder="Latest Stories"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const { data } = await api.put("/merchant/store-blog", {
                        show_on_homepage: cfg.store_blog?.show_on_homepage !== false,
                        heading: cfg.store_blog?.heading || "Latest Stories",
                      });
                      setCfg((c) => ({ ...c, store_blog: data.store_blog }));
                      toast.success("Blog section saved");
                    } catch (e) { toast.error(extractErr(e)); }
                  }}
                  data-testid="hp-blog-save"
                >
                  Save blog section
                </Button>
              </div>
            )}

            <Button onClick={() => save("homepage", "homepage")} data-testid="hp-save" className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> {t("settings.saveHomepage")}</Button>
          </Card>
          )}
        </TabsContent>

        <TabsContent value="topbar">
          <Card className="p-6 space-y-6 border-slate-200/70">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">Announcement bar</div>
                  <div className="text-xs text-slate-500">Thin bar at the very top — for promotions, free shipping, holiday hours.</div>
                </div>
                <Switch checked={!!cfg.header.announcement?.enabled} onCheckedChange={(v) => setPart("header", { ...cfg.header, announcement: { ...(cfg.header.announcement || {}), enabled: v } })} data-testid="hdr-ann-enabled" />
              </div>
              <div className={`space-y-4 ${cfg.header.announcement?.enabled ? "" : "opacity-60 pointer-events-none"}`}>
                <div className="grid grid-cols-2 gap-2 max-w-xs">
                  <div><Label>Background</Label><Input type="color" data-testid="hdr-ann-bg" className="mt-1 h-10 p-1" value={cfg.header.announcement?.bg_color || "#0F172A"} onChange={(e) => setPart("header", { ...cfg.header, announcement: { ...(cfg.header.announcement || {}), bg_color: e.target.value } })} /></div>
                  <div><Label>Text</Label><Input type="color" data-testid="hdr-ann-fg" className="mt-1 h-10 p-1" value={cfg.header.announcement?.text_color || "#FFFFFF"} onChange={(e) => setPart("header", { ...cfg.header, announcement: { ...(cfg.header.announcement || {}), text_color: e.target.value } })} /></div>
                </div>
                <div>
                  <Label>Default icon</Label>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">Used on slides that do not set their own icon. Choose No icon to hide it.</p>
                  <WhyUsIconPicker
                    value={cfg.header.announcement?.icon === undefined ? "Megaphone" : (cfg.header.announcement?.icon || "")}
                    names={ANNOUNCEMENT_LUCIDE_NAMES}
                    allowNone
                    testId="hdr-ann-icon"
                    onChange={(icon) => {
                      const slides = announcementSlides(cfg.header.announcement).map((s) => ({ ...s, icon: s.icon === undefined ? icon : s.icon }));
                      const first = slides[0] || { text: "", link: "" };
                      setPart("header", { ...cfg.header, announcement: { ...(cfg.header.announcement || {}), icon, slides, text: first.text || cfg.header.announcement?.text || "", link: first.link || cfg.header.announcement?.link || "" } });
                    }}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div>
                      <Label>Messages</Label>
                      <p className="text-xs text-slate-500 mt-0.5">One message stays still. Two or more rotate as a slider.</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="hdr-ann-slide-add"
                      disabled={announcementSlides(cfg.header.announcement).length >= 8}
                      onClick={() => {
                        const icon = cfg.header.announcement?.icon === undefined ? "Megaphone" : (cfg.header.announcement?.icon || "");
                        const slides = [...announcementSlides(cfg.header.announcement), { text: "", link: "", icon }];
                        const first = slides[0];
                        setPart("header", { ...cfg.header, announcement: { ...(cfg.header.announcement || {}), slides, text: first.text, link: first.link } });
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add message
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {announcementSlides(cfg.header.announcement).map((sl, i) => (
                      <div key={i} className="p-4 rounded-lg border border-slate-200 bg-slate-50/50 space-y-3" data-testid={`hdr-ann-slide-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-slate-700">Message #{i + 1}{announcementSlides(cfg.header.announcement).length > 1 ? " (slides)" : ""}</div>
                          <button
                            type="button"
                            className="text-red-600 text-xs"
                            data-testid={`hdr-ann-slide-remove-${i}`}
                            disabled={announcementSlides(cfg.header.announcement).length <= 1}
                            onClick={() => {
                              const slides = announcementSlides(cfg.header.announcement).filter((_, k) => k !== i);
                              const first = slides[0] || { text: "", link: "", icon: "" };
                              setPart("header", { ...cfg.header, announcement: { ...(cfg.header.announcement || {}), slides, text: first.text, link: first.link } });
                            }}
                          >
                            Remove
                          </button>
                        </div>
                        <div><Label className="text-xs">Text</Label><Input data-testid={i === 0 ? "hdr-ann-text" : `hdr-ann-text-${i}`} className="mt-1" value={sl.text || ""} onChange={(e) => {
                          const slides = announcementSlides(cfg.header.announcement).map((row, k) => k === i ? { ...row, text: e.target.value } : row);
                          const first = slides[0];
                          setPart("header", { ...cfg.header, announcement: { ...(cfg.header.announcement || {}), slides, text: first.text, link: first.link } });
                        }} placeholder="Free shipping on orders above ₹999" /></div>
                        <div><Label className="text-xs">Click-through link (optional)</Label><Input data-testid={i === 0 ? "hdr-ann-link" : `hdr-ann-link-${i}`} className="mt-1 font-mono-nums" value={sl.link || ""} onChange={(e) => {
                          const slides = announcementSlides(cfg.header.announcement).map((row, k) => k === i ? { ...row, link: e.target.value } : row);
                          const first = slides[0];
                          setPart("header", { ...cfg.header, announcement: { ...(cfg.header.announcement || {}), slides, text: first.text, link: first.link } });
                        }} placeholder="page/offer or /category/sale" /></div>
                        <div>
                          <Label className="text-xs">Icon for this message</Label>
                          <div className="mt-2">
                            <WhyUsIconPicker
                              value={sl.icon === undefined ? (cfg.header.announcement?.icon === undefined ? "Megaphone" : (cfg.header.announcement?.icon || "")) : sl.icon}
                              names={ANNOUNCEMENT_LUCIDE_NAMES}
                              allowNone
                              testId={`hdr-ann-slide-icon-${i}`}
                              onChange={(icon) => {
                                const slides = announcementSlides(cfg.header.announcement).map((row, k) => k === i ? { ...row, icon } : row);
                                const first = slides[0];
                                setPart("header", { ...cfg.header, announcement: { ...(cfg.header.announcement || {}), slides, text: first.text, link: first.link } });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="h-px bg-slate-200" />

            <div>
              <div className="font-semibold text-slate-900 mb-1">What to show in the top bar</div>
              <div className="text-xs text-slate-500 mb-3">Toggle elements on or off. Everything is on by default.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  ["sticky", "Sticky (stays at top when scrolling)"],
                  ["show_search", "Search bar"],
                  ["show_phone", "Phone number"],
                  ["show_whatsapp", "WhatsApp button"],
                  ["show_account", "Sign in / Account"],
                  ["show_cart", "Cart"],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <span className="text-sm text-slate-700">{label}</span>
                    <Switch checked={cfg.header[k] !== false} onCheckedChange={(v) => setPart("header", { ...cfg.header, [k]: v })} data-testid={`hdr-toggle-${k}`} />
                  </label>
                ))}
              </div>
            </div>

            <Button onClick={() => save("header", "header")} data-testid="hdr-save" className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> {t("settings.saveTopbar")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="menu">
          <Card className="p-6 space-y-6 border-slate-200/70">
            <MenuEditor
              title="Main menu"
              subtitle="These links appear in the top navigation on desktop and inside the mobile menu."
              items={cfg.header.menu || []}
              onChange={(m) => setPart("header", { ...cfg.header, menu: m })}
              prefix="menu"
            />
            <div className="h-px bg-slate-200" />
            <MenuEditor
              title="Utility menu (optional)"
              subtitle="Small secondary links — e.g., Track Order, Help. Rendered above the main menu on desktop."
              items={cfg.header.utility_menu || []}
              onChange={(m) => setPart("header", { ...cfg.header, utility_menu: m })}
              prefix="util"
            />
            <Button
              onClick={async () => {
                try {
                  await api.put("/merchant/config/menu", { menu: cfg.header.menu, utility_menu: cfg.header.utility_menu });
                  toast.success("Saved");
                } catch (e) { toast.error(extractErr(e)); }
              }}
              data-testid="menu-save" className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-1" /> {t("settings.saveMenu")}
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="footer">
          <Card className="p-6 space-y-6 border-slate-200/70">
            <div>
              <div className="font-semibold text-slate-900 mb-1">About your store</div>
              <div className="text-xs text-slate-500 mb-2">One or two lines shown in the first footer column.</div>
              <Textarea data-testid="ft-about" rows={3} value={cfg.footer.about || ""} onChange={(e) => setPart("footer", { ...cfg.footer, about: e.target.value })} placeholder="Thoughtfully curated products, delivered fresh to your doorstep." />
            </div>

            <div className="h-px bg-slate-200" />

            <div>
              <div className="font-semibold text-slate-900 mb-1">Contact block</div>
              <div className="text-xs text-slate-500 mb-3">Empty fields are auto-hidden on the storefront.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[["phone", "Phone"], ["whatsapp", "WhatsApp number (with country code)"], ["email", "Email"], ["address", "Address"], ["hours", "Business hours"]].map(([k, label]) => (
                  <div key={k} className={k === "address" || k === "hours" ? "md:col-span-2" : ""}>
                    <Label>{label}</Label>
                    <Input data-testid={`ft-contact-${k}`} className="mt-1" value={cfg.footer.contact?.[k] || ""} onChange={(e) => setPart("footer", { ...cfg.footer, contact: { ...(cfg.footer.contact || {}), [k]: e.target.value } })} placeholder={k === "hours" ? "Mon-Sat 9:00 – 18:00" : undefined} />
                  </div>
                ))}
              </div>
            </div>

            <div className="h-px bg-slate-200" />

            <div>
              <div className="font-semibold text-slate-900 mb-1">Social links</div>
              <div className="text-xs text-slate-500 mb-3">Full URLs. Empty ones stay hidden.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[["instagram", "Instagram", "https://instagram.com/…"], ["facebook", "Facebook", "https://facebook.com/…"], ["youtube", "YouTube", "https://youtube.com/@…"], ["twitter", "X (Twitter)", "https://x.com/…"], ["threads", "Threads", "https://threads.net/@…"]].map(([k, label, ph]) => (
                  <div key={k}>
                    <Label>{label}</Label>
                    <Input data-testid={`ft-social-${k}`} className="mt-1 font-mono-nums" value={cfg.footer.socials?.[k] || ""} onChange={(e) => setPart("footer", { ...cfg.footer, socials: { ...(cfg.footer.socials || {}), [k]: e.target.value } })} placeholder={ph} />
                  </div>
                ))}
              </div>
            </div>

            <div className="h-px bg-slate-200" />

            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-slate-900">Link columns</div>
                  <div className="text-xs text-slate-500">Up to 3 columns of links.</div>
                </div>
                {(cfg.footer.columns || []).length < 3 && (
                  <Button size="sm" variant="outline" onClick={() => setPart("footer", { ...cfg.footer, columns: [...(cfg.footer.columns || []), { title: "New column", links: [] }] })} data-testid="ft-col-add"><Plus className="w-4 h-4 mr-1" />Add column</Button>
                )}
              </div>
              <div className="space-y-3">
                {(cfg.footer.columns || []).map((col, ci) => (
                  <div key={ci} className="p-3 rounded-lg bg-slate-50 space-y-2" data-testid={`ft-col-${ci}`}>
                    <div className="flex items-center gap-2">
                      <Input value={col.title} onChange={(e) => { const cols = [...cfg.footer.columns]; cols[ci] = { ...col, title: e.target.value }; setPart("footer", { ...cfg.footer, columns: cols }); }} placeholder="Column title" className="max-w-xs" data-testid={`ft-col-${ci}-title`} />
                      <button type="button" onClick={() => setPart("footer", { ...cfg.footer, columns: cfg.footer.columns.filter((_, i) => i !== ci) })} className="ml-auto text-red-600 text-xs hover:underline" data-testid={`ft-col-${ci}-remove`}>Remove column</button>
                    </div>
                    <div className="space-y-1.5">
                      {(col.links || []).map((lk, li) => (
                        <div key={li} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Input value={lk.label} onChange={(e) => { const cols = [...cfg.footer.columns]; const links = [...(col.links || [])]; links[li] = { ...lk, label: e.target.value }; cols[ci] = { ...col, links }; setPart("footer", { ...cfg.footer, columns: cols }); }} placeholder="Label" className="flex-1" data-testid={`ft-col-${ci}-lk-${li}-label`} />
                            <Input value={lk.path} onChange={(e) => { const cols = [...cfg.footer.columns]; const links = [...(col.links || [])]; links[li] = { ...lk, path: e.target.value }; cols[ci] = { ...col, links }; setPart("footer", { ...cfg.footer, columns: cols }); }} placeholder="page/about  or  /category/sale  or  https://…" className="flex-1 font-mono-nums" data-testid={`ft-col-${ci}-lk-${li}-path`} />
                            <button type="button" onClick={() => { const cols = [...cfg.footer.columns]; const links = (col.links || []).filter((_, i) => i !== li); cols[ci] = { ...col, links }; setPart("footer", { ...cfg.footer, columns: cols }); }} className="text-red-600 text-sm px-2" data-testid={`ft-col-${ci}-lk-${li}-remove`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={lk.open_in_new_tab === true || (lk.open_in_new_tab !== false && /^https?:\/\//i.test(lk.path || ""))}
                              onChange={(e) => { const cols = [...cfg.footer.columns]; const links = [...(col.links || [])]; links[li] = { ...lk, open_in_new_tab: e.target.checked }; cols[ci] = { ...col, links }; setPart("footer", { ...cfg.footer, columns: cols }); }}
                              data-testid={`ft-col-${ci}-lk-${li}-new-tab`}
                            />
                            Open in a new tab
                          </label>
                        </div>
                      ))}
                      <Button size="sm" variant="outline" onClick={() => { const cols = [...cfg.footer.columns]; cols[ci] = { ...col, links: [...(col.links || []), { label: "New link", path: "" }] }; setPart("footer", { ...cfg.footer, columns: cols }); }} data-testid={`ft-col-${ci}-lk-add`}><Plus className="w-3.5 h-3.5 mr-1" />Add link</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="h-px bg-slate-200" />

            <div>
              <Label>Copyright line (optional)</Label>
              <Input data-testid="ft-copyright" className="mt-1" value={cfg.footer.copyright_text || ""} onChange={(e) => setPart("footer", { ...cfg.footer, copyright_text: e.target.value })} placeholder="© 2026 Your Store · All rights reserved" />
              <div className="text-xs text-slate-500 mt-1">Leave blank to auto-generate from your store name.</div>
            </div>

            <Button onClick={() => save("footer", "footer")} data-testid="ft-save" className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> {t("settings.saveFooter")}</Button>
          </Card>
        </TabsContent>


        <TabsContent value="payment">
          <Card className="p-6 space-y-4 border-slate-200/70">
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
              <div><div className="font-semibold text-slate-900">Cash on Delivery</div><div className="text-xs text-slate-500">Buyers pay in cash when they receive the order.</div></div>
              <Switch checked={cfg.payment.cod_enabled} onCheckedChange={(v) => setPart("payment", { ...cfg.payment, cod_enabled: v })} data-testid="pay-cod" />
            </div>
            <div className="p-4 rounded-lg bg-slate-50 space-y-3">
              <div className="flex items-center justify-between"><div><div className="font-semibold text-slate-900">Razorpay live checkout</div><div className="text-xs text-slate-500">Accept UPI, cards, netbanking, wallets — via your own Razorpay account.</div></div><Switch checked={cfg.payment.razorpay_enabled} onCheckedChange={(v) => setPart("payment", { ...cfg.payment, razorpay_enabled: v })} data-testid="pay-razorpay" /></div>
              <div><Label>Razorpay Key ID</Label><Input data-testid="pay-razorpay-key" className="mt-1 font-mono-nums" value={cfg.payment.razorpay_key_id || ""} onChange={(e) => setPart("payment", { ...cfg.payment, razorpay_key_id: e.target.value })} placeholder="rzp_live_..." /></div>
              <div>
                <Label className="flex items-center gap-2">Razorpay Key Secret
                  {cfg.payment.has_razorpay_key_secret && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_razorpay_key_secret && <button type="button" data-testid="pay-clear-secret" onClick={() => setPart("payment", { ...cfg.payment, razorpay_key_secret: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-razorpay-secret" className="mt-1 font-mono-nums" value={cfg.payment.razorpay_key_secret === "__CLEAR__" ? "" : (cfg.payment.razorpay_key_secret || "")} onChange={(e) => setPart("payment", { ...cfg.payment, razorpay_key_secret: e.target.value })} placeholder={cfg.payment.razorpay_key_secret === "__CLEAR__" ? "(will clear on save)" : (cfg.payment.has_razorpay_key_secret ? "•••••••••••••••• (leave blank to keep existing)" : "Enter your secret")} />
                <div className="text-xs text-slate-500 mt-1">Never shown after saving. Stored server-side, never sent to the browser.</div>
              </div>
              <div>
                <Label className="flex items-center gap-2">Webhook Secret
                  {cfg.payment.has_razorpay_webhook_secret && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_razorpay_webhook_secret && <button type="button" data-testid="pay-clear-webhook" onClick={() => setPart("payment", { ...cfg.payment, razorpay_webhook_secret: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-razorpay-webhook" className="mt-1 font-mono-nums" value={cfg.payment.razorpay_webhook_secret === "__CLEAR__" ? "" : (cfg.payment.razorpay_webhook_secret || "")} onChange={(e) => setPart("payment", { ...cfg.payment, razorpay_webhook_secret: e.target.value })} placeholder={cfg.payment.razorpay_webhook_secret === "__CLEAR__" ? "(will clear on save)" : (cfg.payment.has_razorpay_webhook_secret ? "•••••••••••••••• (leave blank to keep existing)" : "For webhook signature verification")} />
                <div className="text-xs text-slate-500 mt-1">Set this in your Razorpay dashboard webhook. Endpoint: <span className="font-mono">/api/store/&#123;your-slug&#125;/webhooks/razorpay</span></div>
              </div>
            </div>
            {/* Cashfree */}
            <div className="p-4 rounded-lg bg-slate-50 space-y-3">
              <div className="flex items-center justify-between"><div><div className="font-semibold text-slate-900">Cashfree</div><div className="text-xs text-slate-500">Accept UPI, cards, netbanking — via your own Cashfree account.</div></div><Switch checked={cfg.payment.cashfree_enabled} onCheckedChange={(v) => setPart("payment", { ...cfg.payment, cashfree_enabled: v })} data-testid="pay-cashfree" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>App ID</Label><Input data-testid="pay-cf-appid" className="mt-1 font-mono-nums" value={cfg.payment.cashfree_app_id || ""} onChange={(e) => setPart("payment", { ...cfg.payment, cashfree_app_id: e.target.value })} placeholder="TEST... / prod app id" /></div>
                <div><Label>Mode</Label>
                  <select data-testid="pay-cf-mode" className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={cfg.payment.cashfree_mode || "sandbox"} onChange={(e) => setPart("payment", { ...cfg.payment, cashfree_mode: e.target.value })}>
                    <option value="sandbox">Sandbox (test)</option>
                    <option value="production">Production (live)</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2">Secret Key
                  {cfg.payment.has_cashfree_secret_key && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_cashfree_secret_key && <button type="button" onClick={() => setPart("payment", { ...cfg.payment, cashfree_secret_key: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-cf-secret" className="mt-1 font-mono-nums" value={cfg.payment.cashfree_secret_key === "__CLEAR__" ? "" : (cfg.payment.cashfree_secret_key || "")} onChange={(e) => setPart("payment", { ...cfg.payment, cashfree_secret_key: e.target.value })} placeholder={cfg.payment.has_cashfree_secret_key ? "•••••• (leave blank to keep existing)" : "cfsk_..."} />
              </div>
              <div className="text-xs text-slate-500">Webhook endpoint: <span className="font-mono">/api/store/&#123;your-slug&#125;/webhooks/cashfree/&#123;order_id&#125;</span></div>
            </div>

            {/* Instamojo */}
            <div className="p-4 rounded-lg bg-slate-50 space-y-3">
              <div className="flex items-center justify-between"><div><div className="font-semibold text-slate-900">Instamojo</div><div className="text-xs text-slate-500">Hosted checkout via your own Instamojo account.</div></div><Switch checked={cfg.payment.instamojo_enabled} onCheckedChange={(v) => setPart("payment", { ...cfg.payment, instamojo_enabled: v })} data-testid="pay-instamojo" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>API Key</Label><Input data-testid="pay-im-key" className="mt-1 font-mono-nums" value={cfg.payment.instamojo_api_key || ""} onChange={(e) => setPart("payment", { ...cfg.payment, instamojo_api_key: e.target.value })} placeholder="Your API key" /></div>
                <div><Label>Mode</Label>
                  <select data-testid="pay-im-mode" className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={cfg.payment.instamojo_mode || "sandbox"} onChange={(e) => setPart("payment", { ...cfg.payment, instamojo_mode: e.target.value })}>
                    <option value="sandbox">Sandbox (test)</option>
                    <option value="production">Production (live)</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2">Auth Token
                  {cfg.payment.has_instamojo_auth_token && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_instamojo_auth_token && <button type="button" onClick={() => setPart("payment", { ...cfg.payment, instamojo_auth_token: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-im-token" className="mt-1 font-mono-nums" value={cfg.payment.instamojo_auth_token === "__CLEAR__" ? "" : (cfg.payment.instamojo_auth_token || "")} onChange={(e) => setPart("payment", { ...cfg.payment, instamojo_auth_token: e.target.value })} placeholder={cfg.payment.has_instamojo_auth_token ? "•••••• (leave blank to keep existing)" : "Your auth token"} />
              </div>
              <div>
                <Label className="flex items-center gap-2">Private Salt <span className="text-slate-400 text-xs">webhook</span>
                  {cfg.payment.has_instamojo_private_salt && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_instamojo_private_salt && <button type="button" onClick={() => setPart("payment", { ...cfg.payment, instamojo_private_salt: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-im-salt" className="mt-1 font-mono-nums" value={cfg.payment.instamojo_private_salt === "__CLEAR__" ? "" : (cfg.payment.instamojo_private_salt || "")} onChange={(e) => setPart("payment", { ...cfg.payment, instamojo_private_salt: e.target.value })} placeholder={cfg.payment.has_instamojo_private_salt ? "•••••• (leave blank to keep existing)" : "For webhook verification"} />
              </div>
            </div>

            {/* PayU */}
            <div className="p-4 rounded-lg bg-slate-50 space-y-3">
              <div className="flex items-center justify-between"><div><div className="font-semibold text-slate-900">PayU</div><div className="text-xs text-slate-500">Hosted checkout via your own PayU (PayUMoney) account.</div></div><Switch checked={cfg.payment.payu_enabled} onCheckedChange={(v) => setPart("payment", { ...cfg.payment, payu_enabled: v })} data-testid="pay-payu" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Merchant Key</Label><Input data-testid="pay-payu-key" className="mt-1 font-mono-nums" value={cfg.payment.payu_merchant_key || ""} onChange={(e) => setPart("payment", { ...cfg.payment, payu_merchant_key: e.target.value })} placeholder="Your PayU key" /></div>
                <div><Label>Mode</Label>
                  <select data-testid="pay-payu-mode" className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={cfg.payment.payu_mode || "sandbox"} onChange={(e) => setPart("payment", { ...cfg.payment, payu_mode: e.target.value })}>
                    <option value="sandbox">Sandbox (test)</option>
                    <option value="production">Production (live)</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2">Salt
                  {cfg.payment.has_payu_salt && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_payu_salt && <button type="button" onClick={() => setPart("payment", { ...cfg.payment, payu_salt: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-payu-salt" className="mt-1 font-mono-nums" value={cfg.payment.payu_salt === "__CLEAR__" ? "" : (cfg.payment.payu_salt || "")} onChange={(e) => setPart("payment", { ...cfg.payment, payu_salt: e.target.value })} placeholder={cfg.payment.has_payu_salt ? "•••••• (leave blank to keep existing)" : "Your PayU salt (v1/v2)"} />
              </div>
            </div>

            {/* PhonePe Standard Checkout v2 */}
            <div className="p-4 rounded-lg bg-slate-50 space-y-3">
              <div className="flex items-center justify-between"><div><div className="font-semibold text-slate-900">PhonePe</div><div className="text-xs text-slate-500">Latest Standard Checkout (OAuth) — UPI, cards, netbanking via your PhonePe PG account.</div></div><Switch checked={Boolean(cfg.payment.phonepe_enabled)} onCheckedChange={(v) => setPart("payment", { ...cfg.payment, phonepe_enabled: v })} data-testid="pay-phonepe" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Client ID</Label><Input data-testid="pay-pp-client-id" className="mt-1 font-mono-nums" value={cfg.payment.phonepe_client_id || ""} onChange={(e) => setPart("payment", { ...cfg.payment, phonepe_client_id: e.target.value })} placeholder="From PhonePe Business dashboard" /></div>
                <div><Label>Client version</Label><Input data-testid="pay-pp-client-version" className="mt-1 font-mono-nums" value={cfg.payment.phonepe_client_version || "1"} onChange={(e) => setPart("payment", { ...cfg.payment, phonepe_client_version: e.target.value })} placeholder="1" /></div>
              </div>
              <div><Label>Mode</Label>
                <select data-testid="pay-pp-mode" className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={cfg.payment.phonepe_mode || "sandbox"} onChange={(e) => setPart("payment", { ...cfg.payment, phonepe_mode: e.target.value })}>
                  <option value="sandbox">Sandbox (UAT)</option>
                  <option value="production">Production (live)</option>
                </select>
              </div>
              <div>
                <Label className="flex items-center gap-2">Client secret
                  {cfg.payment.has_phonepe_client_secret && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_phonepe_client_secret && <button type="button" onClick={() => setPart("payment", { ...cfg.payment, phonepe_client_secret: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-pp-secret" className="mt-1 font-mono-nums" value={cfg.payment.phonepe_client_secret === "__CLEAR__" ? "" : (cfg.payment.phonepe_client_secret || "")} onChange={(e) => setPart("payment", { ...cfg.payment, phonepe_client_secret: e.target.value })} placeholder={cfg.payment.has_phonepe_client_secret ? "•••••• (leave blank to keep existing)" : "Client secret"} />
              </div>
              <div className="text-xs text-slate-500">Webhook: <span className="font-mono">/api/store/&#123;your-slug&#125;/webhooks/phonepe</span> — set this as the S2S callback in PhonePe.</div>
            </div>

            {/* Stripe Checkout */}
            <div className="p-4 rounded-lg bg-slate-50 space-y-3" data-testid="pay-stripe-section">
              <div className="flex items-center justify-between"><div><div className="font-semibold text-slate-900">Stripe</div><div className="text-xs text-slate-500">Hosted Stripe Checkout — cards and wallets in the currency of the order. Use your own Stripe secret key (sk_test_ or sk_live_).</div></div><Switch checked={Boolean(cfg.payment.stripe_enabled)} onCheckedChange={(v) => setPart("payment", { ...cfg.payment, stripe_enabled: v })} data-testid="pay-stripe" /></div>
              <div>
                <Label className="flex items-center gap-2">Secret key
                  {cfg.payment.has_stripe_secret_key && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_stripe_secret_key && <button type="button" onClick={() => setPart("payment", { ...cfg.payment, stripe_secret_key: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-stripe-secret" className="mt-1 font-mono-nums" value={cfg.payment.stripe_secret_key === "__CLEAR__" ? "" : (cfg.payment.stripe_secret_key || "")} onChange={(e) => setPart("payment", { ...cfg.payment, stripe_secret_key: e.target.value })} placeholder={cfg.payment.has_stripe_secret_key ? "•••••• (leave blank to keep existing)" : "sk_test_... or sk_live_..."} />
              </div>
              <div>
                <Label className="flex items-center gap-2">Webhook signing secret <span className="text-slate-400 font-normal">(optional)</span>
                  {cfg.payment.has_stripe_webhook_secret && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_stripe_webhook_secret && <button type="button" onClick={() => setPart("payment", { ...cfg.payment, stripe_webhook_secret: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-stripe-webhook" className="mt-1 font-mono-nums" value={cfg.payment.stripe_webhook_secret === "__CLEAR__" ? "" : (cfg.payment.stripe_webhook_secret || "")} onChange={(e) => setPart("payment", { ...cfg.payment, stripe_webhook_secret: e.target.value })} placeholder={cfg.payment.has_stripe_webhook_secret ? "•••••• (leave blank to keep existing)" : "whsec_..."} />
              </div>
              <div className="text-xs text-slate-500">Webhook: <span className="font-mono">/api/store/&#123;your-slug&#125;/webhooks/stripe</span> — listen for <span className="font-mono">checkout.session.completed</span>. Shoppers are also confirmed when they return from Checkout.</div>
            </div>

            {/* PayPal */}
            <div className="p-4 rounded-lg bg-slate-50 space-y-3" data-testid="pay-paypal-section">
              <div className="flex items-center justify-between"><div><div className="font-semibold text-slate-900">PayPal</div><div className="text-xs text-slate-500">PayPal Checkout — the shopper approves on PayPal, then we capture the payment on your account.</div></div><Switch checked={Boolean(cfg.payment.paypal_enabled)} onCheckedChange={(v) => setPart("payment", { ...cfg.payment, paypal_enabled: v })} data-testid="pay-paypal" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Client ID</Label><Input data-testid="pay-paypal-client-id" className="mt-1 font-mono-nums" value={cfg.payment.paypal_client_id || ""} onChange={(e) => setPart("payment", { ...cfg.payment, paypal_client_id: e.target.value })} placeholder="From the PayPal developer dashboard" /></div>
                <div><Label>Mode</Label>
                  <select data-testid="pay-paypal-mode" className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={cfg.payment.paypal_mode === "live" || cfg.payment.paypal_mode === "production" ? "live" : "sandbox"} onChange={(e) => setPart("payment", { ...cfg.payment, paypal_mode: e.target.value })}>
                    <option value="sandbox">Sandbox</option>
                    <option value="live">Live</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2">Client secret
                  {cfg.payment.has_paypal_client_secret && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.payment.has_paypal_client_secret && <button type="button" onClick={() => setPart("payment", { ...cfg.payment, paypal_client_secret: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="pay-paypal-secret" className="mt-1 font-mono-nums" value={cfg.payment.paypal_client_secret === "__CLEAR__" ? "" : (cfg.payment.paypal_client_secret || "")} onChange={(e) => setPart("payment", { ...cfg.payment, paypal_client_secret: e.target.value })} placeholder={cfg.payment.has_paypal_client_secret ? "•••••• (leave blank to keep existing)" : "Client secret"} />
              </div>
              <div className="text-xs text-slate-500">Optional webhook: <span className="font-mono">/api/store/&#123;your-slug&#125;/webhooks/paypal</span>. Checkout still captures the payment when the shopper returns.</div>
            </div>

            {/* WhatsApp checkout */}
            <div className="p-4 rounded-lg bg-slate-50 space-y-3">
              <div className="flex items-center justify-between"><div><div className="font-semibold text-slate-900">WhatsApp checkout</div><div className="text-xs text-slate-500">Opens api.whatsapp.com with the order details. The order is saved as a WhatsApp order and needs manual confirmation in Orders.</div></div><Switch checked={Boolean(cfg.payment.whatsapp_enabled)} onCheckedChange={(v) => setPart("payment", { ...cfg.payment, whatsapp_enabled: v })} data-testid="pay-whatsapp" /></div>
              <div><Label>WhatsApp number</Label><Input data-testid="pay-wa-number" className="mt-1 font-mono-nums" value={cfg.payment.whatsapp_number || ""} onChange={(e) => setPart("payment", { ...cfg.payment, whatsapp_number: e.target.value })} placeholder="9198XXXXXXXX (country code, no +)" /></div>
              <p className="text-xs text-slate-500">Leave blank to use the number from Store info. Shoppers are sent to <span className="font-mono">api.whatsapp.com/send</span>.</p>
              <div><Label>Message intro <span className="text-slate-400 font-normal">(optional)</span></Label><Textarea data-testid="pay-wa-message" className="mt-1" rows={2} value={cfg.payment.whatsapp_message || ""} onChange={(e) => setPart("payment", { ...cfg.payment, whatsapp_message: e.target.value })} placeholder="Hi! I'd like to place this order:" /></div>
            </div>

            <Button onClick={() => save("payment", "payment")} data-testid="pay-save" className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> {t("settings.savePayment")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="tax">
          <Card className="p-6 space-y-4 border-slate-200/70" data-testid="settings-tax">
            {(() => {
              const tax = cfg.checkout_charges || { items: [] };
              const items = Array.isArray(tax.items) ? tax.items : [];
              const setItems = (next) => setPart("checkout_charges", { ...tax, items: next });
              const newId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `tax_${Math.random().toString(36).slice(2, 10)}`);
              return (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-900">Checkout charges</div>
                      <p className="text-xs text-slate-500 mt-1 max-w-xl">
                        Add GST, a service fee, packing, or any other amount you collect at checkout. Each row uses the name you type, so the storefront shows that label. A percentage is calculated on the product total after discounts. A fixed amount is added as entered. Shipping stays separate.
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="tax-add"
                      disabled={items.length >= 20}
                      onClick={() => setItems([...items, { id: newId(), name: "", kind: "percent", value: 0, enabled: true }])}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add charge
                    </Button>
                  </div>
                  {items.length === 0 && <div className="text-sm text-slate-400 italic">No extra charges. Checkout shows products and shipping only.</div>}
                  <div className="space-y-2">
                    {items.map((row, i) => (
                      <div key={row.id || i} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px_auto_auto] gap-2 items-center" data-testid={`tax-row-${i}`}>
                        <Input
                          data-testid={`tax-name-${i}`}
                          placeholder="GST, Service fee, Packing…"
                          value={row.name || ""}
                          onChange={(e) => {
                            const next = [...items];
                            next[i] = { ...row, name: e.target.value };
                            setItems(next);
                          }}
                        />
                        <select
                          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                          data-testid={`tax-kind-${i}`}
                          value={row.kind === "flat" ? "flat" : "percent"}
                          onChange={(e) => {
                            const next = [...items];
                            next[i] = { ...row, kind: e.target.value };
                            setItems(next);
                          }}
                        >
                          <option value="percent">Percentage</option>
                          <option value="flat">Fixed amount</option>
                        </select>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="font-mono-nums"
                          data-testid={`tax-value-${i}`}
                          value={row.value ?? ""}
                          onChange={(e) => {
                            const next = [...items];
                            next[i] = { ...row, value: e.target.value };
                            setItems(next);
                          }}
                          placeholder={row.kind === "flat" ? "₹" : "%"}
                        />
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <Switch
                            checked={row.enabled !== false}
                            data-testid={`tax-enabled-${i}`}
                            onCheckedChange={(v) => {
                              const next = [...items];
                              next[i] = { ...row, enabled: v };
                              setItems(next);
                            }}
                          />
                          On
                        </label>
                        <button
                          type="button"
                          className="text-red-600 px-1 justify-self-end"
                          data-testid={`tax-remove-${i}`}
                          onClick={() => setItems(items.filter((_, j) => j !== i))}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button onClick={() => save("checkout_charges", "checkout-charges")} data-testid="tax-save" className="bg-blue-600 hover:bg-blue-700">
                    <Save className="w-4 h-4 mr-1" /> Save charges
                  </Button>
                </>
              );
            })()}
          </Card>
        </TabsContent>

        <TabsContent value="shipping">
          <Card className="p-6 space-y-4 border-slate-200/70">
            {cfg.per_product_shipping_active && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" data-testid="per-product-shipping-paused">
                Per Product Shipping Price is on. Flat rate, free-shipping threshold, India zones, and Shiprocket are paused. Set the shipping price on each product. Checkout adds it to the product price. Turn the addon off to use these settings again.
              </div>
            )}
            <fieldset disabled={Boolean(cfg.per_product_shipping_active)} className={`space-y-4 ${cfg.per_product_shipping_active ? "opacity-60" : ""}`}>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t("settings.flatRate")}</Label><Input type="number" data-testid="sh-flat" className="mt-1 font-mono-nums" value={cfg.shipping.flat_rate} onChange={(e) => setPart("shipping", { ...cfg.shipping, flat_rate: e.target.value })} /></div>
              <div><Label>{t("settings.freeOver")}</Label><Input type="number" data-testid="sh-free" className="mt-1 font-mono-nums" value={cfg.shipping.free_over} onChange={(e) => setPart("shipping", { ...cfg.shipping, free_over: e.target.value })} /></div>
            </div>
            <p className="text-xs text-slate-500 -mt-2">Flat rate is the default when India zones are off, or when a shopper’s pincode/state is not in a zone (unless you restrict delivery below).</p>

            {(() => {
              const ship = cfg.shipping || {};
              const setShip = (patch) => setPart("shipping", { ...ship, ...patch });
              const stateZones = Array.isArray(ship.state_zones) ? ship.state_zones : [];
              const pinZones = Array.isArray(ship.pincode_zones) ? ship.pincode_zones : [];
              const usedStates = new Set(stateZones.map((z) => z.state).filter(Boolean));
              return (
                <div className="p-4 rounded-lg border border-slate-200 space-y-4" data-testid="sh-india-zones">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-900">India delivery zones</div>
                      <div className="text-xs text-slate-500 mt-0.5">Charge by Indian state or 6-digit pincode. Pincode rules win over state rules. Checkout recalculates when the shopper enters location.</div>
                    </div>
                    <Switch checked={Boolean(ship.zones_enabled)} onCheckedChange={(v) => setShip({ zones_enabled: v })} data-testid="sh-zones-enabled" />
                  </div>

                  {Boolean(ship.zones_enabled) && (
                    <>
                      <label className="flex items-start justify-between gap-3 text-sm p-3 rounded-lg bg-slate-50">
                        <span>
                          <span className="font-medium text-slate-900">Only deliver to listed zones</span>
                          <span className="block text-xs text-slate-500 mt-0.5">If on, checkout is blocked when the pincode/state is not in a zone. If off, unlisted locations pay the flat rate.</span>
                        </span>
                        <Switch checked={Boolean(ship.restrict_to_zones)} onCheckedChange={(v) => setShip({ restrict_to_zones: v })} data-testid="sh-restrict-zones" />
                      </label>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-slate-900 text-sm">State rates</div>
                          <Button type="button" size="sm" variant="outline" data-testid="sh-state-add" onClick={() => setShip({ state_zones: [...stateZones, { state: "", rate: 0 }] })}>
                            <Plus className="w-4 h-4 mr-1" /> Add state
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {stateZones.length === 0 && <div className="text-xs text-slate-400 italic">No state rates yet.</div>}
                          {stateZones.map((z, i) => (
                            <div key={i} className="flex items-center gap-2" data-testid={`sh-state-${i}`}>
                              <select
                                className="flex-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                                data-testid={`sh-state-${i}-name`}
                                value={z.state || ""}
                                onChange={(e) => {
                                  const next = [...stateZones];
                                  next[i] = { ...next[i], state: e.target.value };
                                  setShip({ state_zones: next });
                                }}
                              >
                                <option value="">Select state / UT</option>
                                {IN_STATES.map((st) => (
                                  <option key={st} value={st} disabled={usedStates.has(st) && z.state !== st}>{st}</option>
                                ))}
                              </select>
                              <Input
                                type="number"
                                min="0"
                                className="w-28 font-mono-nums"
                                data-testid={`sh-state-${i}-rate`}
                                value={z.rate ?? ""}
                                onChange={(e) => {
                                  const next = [...stateZones];
                                  next[i] = { ...next[i], rate: e.target.value };
                                  setShip({ state_zones: next });
                                }}
                                placeholder="₹"
                              />
                              <button type="button" className="text-red-600 px-1" data-testid={`sh-state-${i}-remove`} onClick={() => setShip({ state_zones: stateZones.filter((_, j) => j !== i) })}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-slate-900 text-sm">Pincode rates</div>
                          <Button type="button" size="sm" variant="outline" data-testid="sh-pin-add" onClick={() => setShip({ pincode_zones: [...pinZones, { pins: "", rate: 0, label: "" }] })}>
                            <Plus className="w-4 h-4 mr-1" /> Add pincodes
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500 mb-2">Use 6-digit pins, commas, or ranges — e.g. <span className="font-mono">400001, 400010-400099</span></p>
                        <div className="space-y-2">
                          {pinZones.length === 0 && <div className="text-xs text-slate-400 italic">No pincode rates yet.</div>}
                          {pinZones.map((z, i) => (
                            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_7rem_auto] gap-2 items-start" data-testid={`sh-pin-${i}`}>
                              <Input
                                data-testid={`sh-pin-${i}-spec`}
                                className="font-mono-nums"
                                value={z.pins || ""}
                                onChange={(e) => {
                                  const next = [...pinZones];
                                  next[i] = { ...next[i], pins: e.target.value };
                                  setShip({ pincode_zones: next });
                                }}
                                placeholder="400001, 400010-400099"
                              />
                              <Input
                                type="number"
                                min="0"
                                className="font-mono-nums"
                                data-testid={`sh-pin-${i}-rate`}
                                value={z.rate ?? ""}
                                onChange={(e) => {
                                  const next = [...pinZones];
                                  next[i] = { ...next[i], rate: e.target.value };
                                  setShip({ pincode_zones: next });
                                }}
                                placeholder="₹"
                              />
                              <button type="button" className="text-red-600 h-10 px-1" data-testid={`sh-pin-${i}-remove`} onClick={() => setShip({ pincode_zones: pinZones.filter((_, j) => j !== i) })}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {(() => {
              const sr = cfg.shipping.shiprocket || {};
              const setSr = (patch) => setPart("shipping", { ...cfg.shipping, shiprocket: { ...sr, ...patch } });
              return <ShiprocketBox sr={sr} setSr={setSr} />;
            })()}

            <Button onClick={() => save("shipping", "shipping")} data-testid="sh-save" className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> {t("settings.saveShipping")}</Button>
            </fieldset>
          </Card>
        </TabsContent>

        <TabsContent value="storefront">
          <Card className="p-6 space-y-5 border-slate-200/70">
            <div>
              <div className="font-semibold text-slate-900">Cart behaviour</div>
              <div className="text-xs text-slate-500 mt-1">Control what happens when a shopper taps &quot;Add to cart&quot;.</div>
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
              <div className="pr-4">
                <Label>Skip cart, go straight to checkout</Label>
                <p className="text-xs text-slate-500 mt-1">When ON, tapping &quot;Add to cart&quot; (which becomes &quot;Buy now&quot;) sends the shopper directly to checkout instead of opening the cart drawer. Great for single-product / express stores.</p>
              </div>
              <Switch checked={Boolean(cfg.preferences?.skip_cart)} onCheckedChange={(v) => setPart("preferences", { ...(cfg.preferences || {}), skip_cart: v })} data-testid="pref-skip-cart" />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
              <div className="pr-4">
                <Label>Guest checkout</Label>
                <p className="text-xs text-slate-500 mt-1">When ON, shoppers can place an order without creating an account. When OFF, an account is created automatically at checkout (or they can sign in) and they&apos;re logged in right after ordering. Off by default.</p>
              </div>
              <Switch checked={Boolean(cfg.preferences?.guest_checkout)} onCheckedChange={(v) => setPart("preferences", { ...(cfg.preferences || {}), guest_checkout: v })} data-testid="pref-guest-checkout" />
            </div>

            <div className="pt-4 border-t border-slate-100">
              <div className="font-semibold text-slate-900">Site font</div>
              <div className="text-xs text-slate-500 mt-1">Choose a Google Font used across your whole storefront (headings &amp; text).</div>
              <div className="mt-3 grid sm:grid-cols-2 gap-4 items-start">
                <div>
                  <Label>Font family</Label>
                  <select
                    data-testid="pref-font"
                    className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={cfg.preferences?.google_font || ""}
                    onChange={(e) => {
                      const f = e.target.value || null;
                      if (f && !document.getElementById(`gf-prev-${f.replace(/\s+/g, "-")}`)) {
                        const l = document.createElement("link"); l.id = `gf-prev-${f.replace(/\s+/g, "-")}`; l.rel = "stylesheet"; l.href = googleFontHref(f); document.head.appendChild(l);
                      }
                      setPart("preferences", { ...(cfg.preferences || {}), google_font: f });
                    }}
                  >
                    <option value="">Default (theme font)</option>
                    {GOOGLE_FONTS.map((f) => (<option key={f} value={f}>{f}</option>))}
                  </select>
                </div>
                <div className="rounded-lg border border-slate-200 p-4 bg-white" style={cfg.preferences?.google_font ? { fontFamily: fontStack(cfg.preferences.google_font) } : {}}>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Preview</div>
                  <div className="text-2xl font-bold text-slate-900 mt-1">{cfg.store_info?.name || "Your Store"}</div>
                  <div className="text-sm text-slate-600 mt-1">Fresh products, delivered with care. 1234567890</div>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-slate-100">
              <div className="font-semibold text-slate-900">WhatsApp chat button</div>
              <div className="text-xs text-slate-500 mt-1">Show a floating &quot;Chat on WhatsApp&quot; button at the bottom-right of your storefront so shoppers can message you in one tap.</div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 mt-3">
                <div className="pr-4">
                  <Label>Enable WhatsApp chat</Label>
                  <p className="text-xs text-slate-500 mt-1">Uses the WhatsApp number set in <b>Store info</b>{cfg.store_info?.whatsapp ? ` (${cfg.store_info.whatsapp})` : " — add one in Store info first"}.</p>
                </div>
                <Switch checked={Boolean(cfg.preferences?.whatsapp_chat_enabled)} onCheckedChange={(v) => setPart("preferences", { ...(cfg.preferences || {}), whatsapp_chat_enabled: v })} data-testid="pref-wa-enabled" />
              </div>
              {cfg.preferences?.whatsapp_chat_enabled && (
                <div className="mt-3">
                  <Label>Pre-filled greeting <span className="text-slate-400 text-xs">optional</span></Label>
                  <Input
                    data-testid="pref-wa-message"
                    className="mt-1"
                    placeholder="Hi! I have a question about your products."
                    value={cfg.preferences?.whatsapp_chat_message || ""}
                    onChange={(e) => setPart("preferences", { ...(cfg.preferences || {}), whatsapp_chat_message: e.target.value })}
                  />
                </div>
              )}
            </div>
            <Button onClick={() => save("preferences", "preferences")} data-testid="pref-save" className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> {t("settings.savePreferences")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="product_settings">
          <Card className="p-6 space-y-8 border-slate-200/70" data-testid="product-settings-card">
            {(() => {
              const ps = cfg.product_settings || { trust_lines: [], feature_icons: [] };
              const setPs = (next) => setPart("product_settings", next);
              const patchLine = (key, i, patch) => {
                const arr = [...(ps[key] || [])];
                arr[i] = { ...arr[i], ...patch };
                setPs({ ...ps, [key]: arr });
              };
              const addLine = (key, max, defaults) => {
                const arr = [...(ps[key] || [])];
                if (arr.length >= max) return;
                arr.push(defaults);
                setPs({ ...ps, [key]: arr });
              };
              const removeLine = (key, i) => setPs({ ...ps, [key]: (ps[key] || []).filter((_, idx) => idx !== i) });
              return (
                <>
                  <TutorialBanner section="products" url={sectionTutorials.products} title={SECTION_TITLES.products} testId="tutorial-banner-products-settings" />
                  <div>
                    <div className="font-semibold text-slate-900">Trust lines</div>
                    <p className="text-xs text-slate-500 mt-1">Up to 2 icon + text rows shown on every product page, just above quantity. Example: Free shipping &amp; returns in the US.</p>
                    <div className="mt-4 space-y-4">
                      {(ps.trust_lines || []).map((row, i) => (
                        <div key={`trust-${i}`} className="p-4 rounded-xl border border-slate-200 bg-slate-50/70 space-y-3" data-testid={`ps-trust-${i}`}>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Line {i + 1}</Label>
                            <button type="button" className="text-xs text-red-600" onClick={() => removeLine("trust_lines", i)} data-testid={`ps-trust-remove-${i}`}>Remove</button>
                          </div>
                          <WhyUsIconPicker value={row.icon || "Truck"} testId={`ps-trust-icons-${i}`} onChange={(icon) => patchLine("trust_lines", i, { icon })} />
                          <Input data-testid={`ps-trust-text-${i}`} value={row.text || ""} onChange={(e) => patchLine("trust_lines", i, { text: e.target.value })} placeholder="Free shipping & returns in the US" />
                        </div>
                      ))}
                      {(ps.trust_lines || []).length < 2 && (
                        <Button type="button" size="sm" variant="outline" data-testid="ps-trust-add" onClick={() => addLine("trust_lines", 2, { icon: (ps.trust_lines || []).length === 0 ? "Truck" : "ShieldCheck", text: "" })}>
                          <Plus className="w-4 h-4 mr-1" /> Add trust line
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="h-px bg-slate-200" />
                  <div>
                    <div className="font-semibold text-slate-900">Feature icons</div>
                    <p className="text-xs text-slate-500 mt-1">Up to 4 side-by-side icons with short labels on every product page (e.g. Odor resistant, Temperature regulating).</p>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      {(ps.feature_icons || []).map((row, i) => (
                        <div key={`feat-${i}`} className="p-4 rounded-xl border border-slate-200 bg-slate-50/70 space-y-3" data-testid={`ps-feature-${i}`}>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Icon {i + 1}</Label>
                            <button type="button" className="text-xs text-red-600" onClick={() => removeLine("feature_icons", i)} data-testid={`ps-feature-remove-${i}`}>Remove</button>
                          </div>
                          <WhyUsIconPicker value={row.icon || "Sparkles"} testId={`ps-feature-icons-${i}`} onChange={(icon) => patchLine("feature_icons", i, { icon })} />
                          <Input data-testid={`ps-feature-text-${i}`} value={row.text || ""} onChange={(e) => patchLine("feature_icons", i, { text: e.target.value })} placeholder="Odor resistant" />
                        </div>
                      ))}
                    </div>
                    {(ps.feature_icons || []).length < 4 && (
                      <Button type="button" size="sm" variant="outline" className="mt-3" data-testid="ps-feature-add" onClick={() => addLine("feature_icons", 4, { icon: "Sparkles", text: "" })}>
                        <Plus className="w-4 h-4 mr-1" /> Add feature icon
                      </Button>
                    )}
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        await api.put("/merchant/config/product-settings", ps);
                        toast.success("Saved");
                      } catch (e) { toast.error(extractErr(e)); }
                    }}
                    data-testid="ps-save"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="w-4 h-4 mr-1" /> {t("settings.saveProductSettings")}
                  </Button>
                </>
              );
            })()}
          </Card>
        </TabsContent>

        <TabsContent value="under_construction">
          <Card className="p-6 space-y-5 border-slate-200/70" data-testid="under-construction-card">
            {(() => {
              const uc = cfg.under_construction || { enabled: false, headline: "", message: "", eta: "", show_whatsapp: true };
              const setUc = (next) => setPart("under_construction", { ...uc, ...next });
              return (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-semibold text-slate-900">Under construction page</div>
                      <p className="text-sm text-slate-500 mt-1">When this is on, shoppers see a branded coming-soon page instead of your shop. Your dashboard stays available.</p>
                    </div>
                    <Switch data-testid="uc-enabled" checked={Boolean(uc.enabled)} onCheckedChange={(v) => setUc({ enabled: v })} />
                  </div>
                  <div>
                    <Label>Headline</Label>
                    <Input data-testid="uc-headline" className="mt-1" value={uc.headline || ""} onChange={(e) => setUc({ headline: e.target.value })} placeholder="We're getting ready" />
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Textarea data-testid="uc-message" className="mt-1" rows={4} value={uc.message || ""} onChange={(e) => setUc({ message: e.target.value })} />
                  </div>
                  <div>
                    <Label>Opening date / note (optional)</Label>
                    <Input data-testid="uc-eta" className="mt-1" value={uc.eta || ""} onChange={(e) => setUc({ eta: e.target.value })} placeholder="Opening this festive season" />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 p-3">
                    <div>
                      <div className="text-sm font-medium">Show WhatsApp</div>
                      <p className="text-xs text-slate-500">Uses the number from Store info.</p>
                    </div>
                    <Switch data-testid="uc-whatsapp" checked={uc.show_whatsapp !== false} onCheckedChange={(v) => setUc({ show_whatsapp: v })} />
                  </div>
                  <Button onClick={() => save("under_construction", "under-construction")} data-testid="uc-save" className="bg-blue-600 hover:bg-blue-700">
                    <Save className="w-4 h-4 mr-1" /> Save under construction
                  </Button>
                </>
              );
            })()}
          </Card>
        </TabsContent>

        <TabsContent value="adult_control">
          <Card className="p-6 space-y-5 border-slate-200/70" data-testid="adult-control-card">
            {(() => {
              const ac = cfg.adult_control || { enabled: false };
              const locked = Boolean(ac.platform_enforced);
              const setAc = (next) => setPart("adult_control", { ...ac, ...next });
              return (
                <>
                  {locked ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" data-testid="adult-control-enforced-banner">
                      This is by default on for your site by our ai detection due to adult products - if you think it is mistaken than please request a support ticket here{" "}
                      <Link to="/admin/support" className="font-semibold underline" data-testid="adult-control-support-link">Support</Link>
                    </div>
                  ) : null}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-semibold text-slate-900">18+ consent popup</div>
                      <p className="text-sm text-slate-500 mt-1">When this is on, shoppers must confirm they are 18 or older before they can browse or buy.</p>
                    </div>
                    <Switch data-testid="adult-enabled" checked={Boolean(ac.enabled) || locked} disabled={locked} onCheckedChange={(v) => { if (!locked) setAc({ enabled: v }); }} />
                  </div>
                  <div>
                    <Label>Popup background image</Label>
                    <div className="mt-2">
                      <UploadImage kind="hero" value={ac.background_image_url || ""} onChange={(url) => setAc({ background_image_url: url })} testid="adult-bg-upload" hint="Optional full-screen backdrop behind the 18+ popup" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Badge</Label>
                      <Input data-testid="adult-badge" className="mt-1" value={ac.badge || ""} onChange={(e) => setAc({ badge: e.target.value })} />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input data-testid="adult-title" className="mt-1" value={ac.title || ""} onChange={(e) => setAc({ title: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input data-testid="adult-subtitle" className="mt-1" value={ac.subtitle || ""} onChange={(e) => setAc({ subtitle: e.target.value })} />
                  </div>
                  <div>
                    <Label>Body</Label>
                    <Textarea data-testid="adult-body" className="mt-1" rows={4} value={ac.body || ""} onChange={(e) => setAc({ body: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Enter button</Label>
                      <Input data-testid="adult-enter-label" className="mt-1" value={ac.enter_label || ""} onChange={(e) => setAc({ enter_label: e.target.value })} />
                      <Input data-testid="adult-enter-sub" className="mt-2" value={ac.enter_sub || ""} onChange={(e) => setAc({ enter_sub: e.target.value })} />
                    </div>
                    <div>
                      <Label>Leave button</Label>
                      <Input data-testid="adult-leave-label" className="mt-1" value={ac.leave_label || ""} onChange={(e) => setAc({ leave_label: e.target.value })} />
                      <Input data-testid="adult-leave-sub" className="mt-2" value={ac.leave_sub || ""} onChange={(e) => setAc({ leave_sub: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Footer note</Label>
                    <Input data-testid="adult-footer" className="mt-1" value={ac.footer || ""} onChange={(e) => setAc({ footer: e.target.value })} />
                  </div>
                  <div>
                    <Label>Restricted title</Label>
                    <Input data-testid="adult-restricted-title" className="mt-1" value={ac.restricted_title || ""} onChange={(e) => setAc({ restricted_title: e.target.value })} />
                  </div>
                  <div>
                    <Label>Restricted message</Label>
                    <Textarea data-testid="adult-restricted-body" className="mt-1" rows={6} value={ac.restricted_body || ""} onChange={(e) => setAc({ restricted_body: e.target.value })} />
                  </div>
                  <Button onClick={() => save("adult_control", "adult-control")} data-testid="adult-save" className="bg-blue-600 hover:bg-blue-700">
                    <Save className="w-4 h-4 mr-1" /> Save adult control
                  </Button>
                </>
              );
            })()}
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <Card className="p-6 space-y-4 border-slate-200/70">
            <TutorialBanner section="email" url={sectionTutorials.email} title={SECTION_TITLES.email} testId="tutorial-banner-email" />
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
              <div className="pr-4">
                <div className="font-semibold text-slate-900">Send emails from your own inbox</div>
                <div className="text-xs text-slate-500">Order confirmations to your customers will go from your address so your brand and deliverability apply. You will also get a <b>new order</b> alert at your admin login email from our platform sender (MeraStartup). When OFF, customer mail still goes via our shared sender.</div>
              </div>
              <Switch checked={!!(cfg.smtp && cfg.smtp.enabled)} onCheckedChange={(v) => setPart("smtp", { ...(cfg.smtp || {}), enabled: v })} data-testid="smtp-enabled" />
            </div>
            <div className={`grid grid-cols-2 gap-4 ${cfg.smtp?.enabled ? "" : "opacity-60 pointer-events-none"}`}>
              <div><Label>SMTP host</Label><Input data-testid="smtp-host" className="mt-1 font-mono-nums" value={cfg.smtp?.host || ""} onChange={(e) => setPart("smtp", { ...(cfg.smtp || {}), host: e.target.value })} placeholder="smtp.gmail.com" /></div>
              <div><Label>Port</Label><Input type="number" data-testid="smtp-port" className="mt-1 font-mono-nums" value={cfg.smtp?.port || 587} onChange={(e) => setPart("smtp", { ...(cfg.smtp || {}), port: parseInt(e.target.value || "587", 10) })} placeholder="587" /></div>
              <div><Label>Username</Label><Input data-testid="smtp-user" className="mt-1 font-mono-nums" value={cfg.smtp?.username || ""} onChange={(e) => setPart("smtp", { ...(cfg.smtp || {}), username: e.target.value })} placeholder="orders@yourstore.com" /></div>
              <div>
                <Label className="flex items-center gap-2">Password / App password
                  {cfg.smtp?.has_password && <span className="text-xs text-emerald-600 font-normal">· stored</span>}
                  {cfg.smtp?.has_password && <button type="button" data-testid="smtp-clear" onClick={() => setPart("smtp", { ...(cfg.smtp || {}), password: "__CLEAR__" })} className="ml-auto text-xs text-red-600 hover:text-red-700">Clear stored</button>}
                </Label>
                <Input type="password" data-testid="smtp-pass" className="mt-1 font-mono-nums" value={cfg.smtp?.password === "__CLEAR__" ? "" : (cfg.smtp?.password || "")} onChange={(e) => setPart("smtp", { ...(cfg.smtp || {}), password: e.target.value })} placeholder={cfg.smtp?.password === "__CLEAR__" ? "(will clear on save)" : (cfg.smtp?.has_password ? "•••••••••••••••• (blank to keep)" : "Your SMTP password")} />
              </div>
              <div><Label>From email</Label><Input data-testid="smtp-from" className="mt-1 font-mono-nums" value={cfg.smtp?.from_email || ""} onChange={(e) => setPart("smtp", { ...(cfg.smtp || {}), from_email: e.target.value })} placeholder="orders@yourstore.com" /></div>
              <div><Label>From name (shown to customer)</Label><Input data-testid="smtp-from-name" className="mt-1" value={cfg.smtp?.from_name || ""} onChange={(e) => setPart("smtp", { ...(cfg.smtp || {}), from_name: e.target.value })} placeholder="Your Store Name" /></div>
              <div className="col-span-2 flex items-center gap-2 text-sm text-slate-700">
                <Switch checked={cfg.smtp?.use_tls !== false} onCheckedChange={(v) => setPart("smtp", { ...(cfg.smtp || {}), use_tls: v })} data-testid="smtp-tls" />
                Use TLS (STARTTLS) — recommended for port 587
              </div>
            </div>
            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">Tip · For Gmail use an <a className="underline" href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noreferrer">App Password</a>, not your account password. Test with a small order first.</div>
            <Button onClick={() => save("smtp", "smtp")} data-testid="smtp-save" className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1" /> {t("settings.saveEmail")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="domain">
          <Card className="p-6 space-y-4 border-slate-200/70">
            <TutorialBanner section="domain" url={sectionTutorials.domain} title={SECTION_TITLES.domain} testId="tutorial-banner-domain" />
            <div>
              <div className="font-semibold text-slate-900">Your own domain</div>
              <div className="text-sm text-slate-500 mt-1">Serve your store from your own domain (e.g. <span className="font-mono">www.yourbrand.com</span>). Your platform URL <span className="font-mono">{platformDisplayHost()}/{tenant?.slug || "your-store"}</span> stays active and redirects to the custom domain once it is verified.</div>
            </div>
            <div>
              <Label>Custom domain (host only)</Label>
              <Input data-testid="dom-input" className="mt-1 font-mono-nums" value={cfg.custom_domain || ""} onChange={(e) => setCfg({ ...cfg, custom_domain: e.target.value })} placeholder="www.yourbrand.com" />
              {cfg.custom_domain && cfg.custom_domain === (cfg._saved_custom_domain ?? cfg.custom_domain) && cfg._saved_custom_domain && (
                <div className="mt-3 text-sm flex items-center gap-3 flex-wrap">
                  <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.custom_domain_verified ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`} data-testid="dom-status">
                    {cfg.custom_domain_verified ? "✓ Verified & live" : "Pending DNS verification"}
                  </div>
                  {!cfg.custom_domain_verified && (
                    <button type="button" onClick={() => verifyDomain({ reload: true })} disabled={domVerifying} className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-60" data-testid="dom-verify-inline">
                      {domVerifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Check DNS now
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2" data-testid="dom-dns-help">
              <div className="font-medium text-slate-800">DNS setup</div>
              <p>Point your domain at <span className="font-mono">{cfg.domain_cname_target || "shops.merastartup.in"}</span> — not at a server IP. If we move servers later, your DNS stays the same.</p>
              <div><span className="font-semibold">1. www (required)</span> — add a <span className="font-mono">CNAME</span>:</div>
              <div className="font-mono bg-white border border-slate-200 rounded p-2 leading-relaxed">
                Type: CNAME<br />
                Host/Name: <span className="text-slate-900">www</span><br />
                Target / Points to: <span className="text-slate-900">{cfg.domain_cname_target || "shops.merastartup.in"}</span><br />
                Proxy / CDN: off (DNS only)<br />
                TTL: Automatic
              </div>
              <div className="pt-1"><span className="font-semibold">2. Root domain (yourbrand.com)</span> — GoDaddy and many registrars cannot CNAME <span className="font-mono">@</span>. Use their <em>forwarding</em> / URL redirect from the root to <span className="font-mono">https://www.yourbrand.com</span>. If the panel offers ALIAS / ANAME / flattened CNAME (Hostinger, Cloudflare), you may instead point <span className="font-mono">@</span> at <span className="font-mono">{cfg.domain_cname_target || "shops.merastartup.in"}</span>. Do <em>not</em> add an A record to an IP for new domains.</div>
              <div className="pt-1">Save this field as <span className="font-mono">www.yourbrand.com</span> (the host with the CNAME). The store is served on www; we do not redirect www back to the root (that loops with registrar forwarding). Then click <em>Save domain</em>. It shows as verified once DNS propagates (usually 5–30 min). SSL is issued automatically after the first visit.</div>
              <div className="text-slate-500">Already using an A record to <span className="font-mono">{cfg.domain_platform_ip || "187.127.153.67"}</span>? That still works — no change required. New domains should use the CNAME above.</div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={async () => {
                  try {
                    await api.put("/merchant/config/domain", { custom_domain: cfg.custom_domain || null });
                    setCfg((c) => ({ ...c, _saved_custom_domain: c.custom_domain || null, custom_domain_verified: false }));
                    toast.success("Saved");
                    if (cfg.custom_domain) await verifyDomain({ reload: true });
                    else await reloadDashboardForDomain();
                  }
                  catch (e) { toast.error(extractErr(e)); }
                }}
                data-testid="dom-save" className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-1" /> {t("settings.saveDomain")}
              </Button>
              {cfg.custom_domain && cfg.custom_domain === cfg._saved_custom_domain && (
                <Button variant="outline" onClick={() => verifyDomain({ reload: true })} disabled={domVerifying} data-testid="dom-verify" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                  {domVerifying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />} Verify DNS now
                </Button>
              )}
              {cfg.custom_domain && <Button variant="outline" onClick={async () => { try { await api.put("/merchant/config/domain", { custom_domain: null }); setCfg((c) => ({ ...c, custom_domain: "", _saved_custom_domain: null, custom_domain_verified: false })); toast.success("Removed"); await reloadDashboardForDomain(); } catch (e) { toast.error(extractErr(e)); } }} data-testid="dom-remove">Remove</Button>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
